/** @format */

import React, { useState, useEffect, useRef } from "react";
import {
  XMarkIcon,
  EyeIcon,
  PencilIcon,
  PhotoIcon,
  TagIcon,
  UserIcon,
  ClockIcon,
  AcademicCapIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  SparklesIcon,
  BookOpenIcon,
  UsersIcon,
  CloudArrowUpIcon,
  ExclamationCircleIcon,
  InformationCircleIcon,
  CurrencyDollarIcon,
  VideoCameraIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";
import { useForm } from "react-hook-form";
import { Editor } from "@tinymce/tinymce-react";

// Load TinyMCE core + theme locally to avoid API key validation
// Import order is important - core first, then themes, then plugins, then skins
import "tinymce/tinymce";
import "tinymce/themes/silver";
import "tinymce/models/dom";
import "tinymce/icons/default";

// Load plugins locally (only the ones we actually use)
import "tinymce/plugins/link";
import "tinymce/plugins/table";
import "tinymce/plugins/autolink";
import "tinymce/plugins/lists";
import "tinymce/plugins/wordcount";

// Load skins locally so it looks like cloud version
import "tinymce/skins/ui/oxide/skin.min.css";
import "tinymce/skins/content/default/content.min.css";

import useCourseUsers from "../hooks/useCourseUsers";
import useTags from "../hooks/useTags";
import useBooks from "../hooks/useBooks";
import { useAuth } from "../contexts/AuthContext";
import { courseEditorConfig } from "../utils/tinymceConfig";
import CourseClassroomManager from "./CourseClassroomManager";
import useCourseClassrooms from "../hooks/useCourseClassrooms";
import useCourseClassroomDetails from "../hooks/useCourseClassroomDetails";
import { toast } from "react-hot-toast";
import useFileUpload from "../hooks/useFileUpload";

const CourseModal = ({
  isOpen,
  onClose,
  onSave,
  course,
  mode = "add", // 'add', 'edit', 'view'
}) => {
  const { currentUser, userRole } = useAuth();
  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm();
  const {
    uploadVideo,
    deleteFile,
    uploading: videoUploading,
    uploadProgress: videoUploadProgress,
  } = useFileUpload();
  const courseVideoInputRef = useRef(null);
  const newLessonVideoInputRef = useRef(null);
  const editingLessonVideoInputRef = useRef(null);

  const [loading, setLoading] = useState(false);
  const [imagePreview, setImagePreview] = useState("");
  const [dragOver, setDragOver] = useState(false);
  const [courseVideoDragOver, setCourseVideoDragOver] = useState(false);
  const [newLessonVideoDragOver, setNewLessonVideoDragOver] = useState(false);
  const [editingLessonVideoDragOver, setEditingLessonVideoDragOver] =
    useState(false);
  const [selectedAuthor, setSelectedAuthor] = useState("");
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [requirements, setRequirements] = useState([]);
  const [learnings, setLearnings] = useState([]);
  const [newRequirement, setNewRequirement] = useState("");
  const [newLearning, setNewLearning] = useState("");
  const [selectedTags, setSelectedTags] = useState([]);
  const [selectedProgram, setSelectedProgram] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [sections, setSections] = useState([]);
  const [showCustomProgramModal, setShowCustomProgramModal] = useState(false);
  const [showCustomCategoryModal, setShowCustomCategoryModal] = useState(false);
  const [showEditProgramModal, setShowEditProgramModal] = useState(false);
  const [showEditCategoryModal, setShowEditCategoryModal] = useState(false);
  const [showDeleteProgramModal, setShowDeleteProgramModal] = useState(false);
  const [showDeleteCategoryModal, setShowDeleteCategoryModal] = useState(false);
  const [customProgram, setCustomProgram] = useState("");
  const [customCategory, setCustomCategory] = useState("");
  const [editProgram, setEditProgram] = useState("");
  const [editCategory, setEditCategory] = useState("");
  const [editingProgram, setEditingProgram] = useState("");
  const [editingCategory, setEditingCategory] = useState("");
  const [deletingProgram, setDeletingProgram] = useState("");
  const [deletingCategory, setDeletingCategory] = useState("");
  const [customCategories, setCustomCategories] = useState({}); // Store custom categories per program
  const [newSection, setNewSection] = useState({ name: "", order: 0 });

  // Program options with state management
  const [programOptions, setProgramOptions] = useState([
    "ICAG",
    "CITG",
    "Corporate Training",
    "Digital Marketing",
  ]);

  // Category options based on selected program
  const getCategoryOptions = (program) => {
    if (!program) return [];

    let baseCategories = [];
    switch (program) {
      case "ICAG":
        baseCategories = ["ICAG - Level 1", "ICAG - Level 2", "ICAG - Level 3"];
        break;
      case "CITG":
        baseCategories = [
          "CITG - Level 1",
          "CITG - Level 2",
          "CITG - Level 3",
          "CITG - Professional Level",
        ];
        break;
      case "Corporate Training":
        baseCategories = ["Corporate Training"];
        break;
      case "Digital Marketing":
        baseCategories = ["Digital Marketing and Business Growth"];
        break;
      default:
        baseCategories = [];
    }

    // Add custom categories for this program
    const customCats = customCategories[program] || [];
    return [...baseCategories, ...customCats];
  };

  // Handle adding custom program
  const handleAddCustomProgram = () => {
    if (customProgram.trim()) {
      const newProgram = customProgram.trim();

      // Add to program options if not already present
      if (!programOptions.includes(newProgram)) {
        setProgramOptions((prev) => [...prev, newProgram]);
      }

      setSelectedProgram(newProgram);
      setCustomProgram("");
      setShowCustomProgramModal(false);
    }
  };

  // Handle adding custom category
  const handleAddCustomCategory = () => {
    if (customCategory.trim() && selectedProgram) {
      const newCategory = customCategory.trim();

      // Add to custom categories for this program
      setCustomCategories((prev) => ({
        ...prev,
        [selectedProgram]: [...(prev[selectedProgram] || []), newCategory],
      }));

      // Set the selected category
      setSelectedCategory(newCategory);
      setCustomCategory("");
      setShowCustomCategoryModal(false);
    }
  };

  // Handle editing program
  const handleEditProgram = () => {
    if (editProgram.trim() && editingProgram) {
      const newProgramName = editProgram.trim();

      // Update program options
      setProgramOptions((prev) =>
        prev.map((prog) => (prog === editingProgram ? newProgramName : prog))
      );

      // Update selected program if it was the one being edited
      if (selectedProgram === editingProgram) {
        setSelectedProgram(newProgramName);
      }

      // Update custom categories if they exist for the old program name
      if (customCategories[editingProgram]) {
        setCustomCategories((prev) => {
          const newCustomCategories = { ...prev };
          newCustomCategories[newProgramName] =
            newCustomCategories[editingProgram];
          delete newCustomCategories[editingProgram];
          return newCustomCategories;
        });
      }

      setEditProgram("");
      setEditingProgram("");
      setShowEditProgramModal(false);
    }
  };

  // Handle editing category
  const handleEditCategory = () => {
    if (editCategory.trim() && editingCategory && selectedProgram) {
      const newCategoryName = editCategory.trim();

      // Update custom categories for this program
      setCustomCategories((prev) => ({
        ...prev,
        [selectedProgram]:
          prev[selectedProgram]?.map((cat) =>
            cat === editingCategory ? newCategoryName : cat
          ) || [],
      }));

      // Update selected category if it was the one being edited
      if (selectedCategory === editingCategory) {
        setSelectedCategory(newCategoryName);
      }

      setEditCategory("");
      setEditingCategory("");
      setShowEditCategoryModal(false);
    }
  };

  // Check if a program is editable (custom programs only)
  const isProgramEditable = (program) => {
    return ![
      "ICAG",
      "CITG",
      "Corporate Training",
      "Digital Marketing",
    ].includes(program);
  };

  // Check if a category is editable (custom categories only)
  const isCategoryEditable = (category) => {
    if (!selectedProgram) return false;
    const baseCategories = getCategoryOptions(selectedProgram).filter(
      (cat) => !customCategories[selectedProgram]?.includes(cat)
    );
    return !baseCategories.includes(category);
  };

  // Handle deleting custom program
  const handleDeleteProgram = () => {
    if (deletingProgram) {
      // Remove from program options
      setProgramOptions((prev) =>
        prev.filter((prog) => prog !== deletingProgram)
      );

      // Clear selected program if it was the one being deleted
      if (selectedProgram === deletingProgram) {
        setSelectedProgram("");
        setSelectedCategory(""); // Also clear category since program is gone
      }

      // Remove custom categories for this program
      setCustomCategories((prev) => {
        const newCustomCategories = { ...prev };
        delete newCustomCategories[deletingProgram];
        return newCustomCategories;
      });

      setDeletingProgram("");
      setShowDeleteProgramModal(false);
    }
  };

  // Handle deleting custom category
  const handleDeleteCategory = () => {
    if (deletingCategory && selectedProgram) {
      // Remove from custom categories for this program
      setCustomCategories((prev) => ({
        ...prev,
        [selectedProgram]:
          prev[selectedProgram]?.filter((cat) => cat !== deletingCategory) ||
          [],
      }));

      // Clear selected category if it was the one being deleted
      if (selectedCategory === deletingCategory) {
        setSelectedCategory("");
      }

      setDeletingCategory("");
      setShowDeleteCategoryModal(false);
    }
  };
  const [newLesson, setNewLesson] = useState({
    name: "",
    description: "",
    order: 0,
    content_type: "video",
    video_url: "",
    quiz: [], // Changed from string to array for structured quiz data
  });
  const [editingSection, setEditingSection] = useState(null);
  const [editingLesson, setEditingLesson] = useState(null);
  const [selectedBooks, setSelectedBooks] = useState([]);
  const [bookSearchTerm, setBookSearchTerm] = useState("");
  const [showBookModal, setShowBookModal] = useState(false);
  const [autoPriceEnabled, setAutoPriceEnabled] = useState(true);
  const [showClassroomManager, setShowClassroomManager] = useState(false);

  // Get classrooms associated with this course with full details
  const { classroomDetails: courseClassrooms, loading: classroomsLoading } =
    useCourseClassroomDetails(course?.id);
  const [editorReady, setEditorReady] = useState(false);
  const [formReady, setFormReady] = useState(false);
  const editorRef = useRef(null);

  // Fetch users for assignment
  const {
    authors,
    students,
    loading: usersLoading,
    getUserById,
  } = useCourseUsers();

  // Fetch tags
  const { tags, loading: tagsLoading, getTagNamesByIds } = useTags();

  // Fetch books
  const { books, loading: booksLoading, searchBooks, getBookById } = useBooks();

  // Mark form as ready after initial render
  useEffect(() => {
    setFormReady(true);
  }, []);

  const watchedPrice = watch("pricee", "0");
  const watchedPriceStatus = watch("price_status", "free");
  const watchedStatus = watch("status", "draft");

  // Helper function to check if editor is ready
  const isEditorReady = () => {
    try {
      const editor = editorRef.current;
      return (
        editor &&
        typeof editor === "object" &&
        typeof editor.setContent === "function" &&
        editor.initialized === true &&
        editorReady
      );
    } catch (error) {
      console.warn("Error checking editor readiness:", error);
      return false;
    }
  };

  // TinyMCE configuration - isolated to prevent plugin conflicts
  const tinyMCEConfig = {
    // Core configuration
    height: 400,
    menubar: false,
    skin: false,
    content_css: false,
    license_key: "gpl",
    promotion: false,
    branding: false,

    // Only use the plugins we explicitly imported
    plugins: ["autolink", "link", "lists", "table", "wordcount"],
    toolbar:
      "undo redo | bold italic underline | link | numlist bullist | table | removeformat",

    // Content styling
    content_style:
      "body { font-family: Helvetica, Arial, sans-serif; font-size: 14px }",
    placeholder: "Enter course description...",

    // Disable external services and validation
    external_plugins: {},
    verify_html: false,
    auto_update: false,
    check_whitelist: false,

    // Prevent external resource loading
    convert_urls: false,
    relative_urls: false,
    remove_script_host: false,
    forced_root_block: "p",

    setup: (editor) => {
      try {
        // Store editor reference only if editor is valid
        if (editor && typeof editor === "object") {
          editorRef.current = editor;

          // Ensure editor is properly initialized
          editor.on("init", () => {
            try {
              if (
                editor &&
                typeof editor.setContent === "function" &&
                course?.meta?.description
              ) {
                editor.setContent(course.meta.description);
              }
              setEditorReady(true);
            } catch (error) {
              console.warn("TinyMCE init error:", error);
            }
          });
        }
      } catch (error) {
        console.warn("TinyMCE setup error:", error);
      }
    },

    // Error handling
    onerror: (error) => {
      try {
        if (error && typeof error === "object") {
          console.warn("TinyMCE error (non-critical):", error);
        }
      } catch (err) {
        console.warn("Error in TinyMCE error handler:", err);
      }
    },
  };

  // Image upload handler
  const handleImageUpload = async (file) => {
    return new Promise((resolve, reject) => {
      if (!file) {
        reject("No file selected");
        return;
      }

      // Validate file type
      if (!file.type.startsWith("image/")) {
        reject("Please select a valid image file");
        return;
      }

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        reject("Image size must be less than 5MB");
        return;
      }

      const reader = new FileReader();
      reader.onload = (e) => {
        const base64 = e.target.result;
        resolve(base64);
      };
      reader.onerror = () => {
        reject("Error reading file");
      };
      reader.readAsDataURL(file);
    });
  };

  // Handle drag and drop for image upload
  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragOver(false);
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    setDragOver(false);

    const files = e.dataTransfer.files;
    if (files.length > 0) {
      try {
        const imageUrl = await handleImageUpload(files[0]);
        setImagePreview(imageUrl);
        setValue("image_url", imageUrl);
      } catch (error) {
        alert("Error uploading image: " + error);
      }
    }
  };

  // Final check for video upload logic
  const processVideoFile = async (file, currentUrl, updateFn, successMsg) => {
    if (!file) return;
    if (!file.type.startsWith("video/")) {
      toast.error("Please select a valid video file");
      return;
    }

    try {
      // If there's an existing video from Firebase Storage, delete it first (delete any trace)
      if (currentUrl && currentUrl.includes("firebasestorage.googleapis.com")) {
        await deleteFile(currentUrl);
      }

      const videoUrl = await uploadVideo(file, file.name);
      updateFn(videoUrl);
      toast.success(successMsg || "Video uploaded successfully");
    } catch (error) {
      toast.error("Error uploading video: " + error.message);
    }
  };

  const handleCourseVideoUpload = async (e) => {
    const file = e.target.files[0];
    const currentUrl = watch("video_url");
    await processVideoFile(
      file,
      currentUrl,
      (url) => setValue("video_url", url, { shouldDirty: true }),
      "Course video uploaded successfully"
    );
  };

  const handleCourseVideoDrop = async (e) => {
    e.preventDefault();
    setCourseVideoDragOver(false);
    const file = e.dataTransfer.files[0];
    const currentUrl = watch("video_url");
    await processVideoFile(
      file,
      currentUrl,
      (url) => setValue("video_url", url, { shouldDirty: true }),
      "Course video uploaded via drag and drop"
    );
  };

  const handleNewLessonVideoUpload = async (e) => {
    const file = e.target.files[0];
    const currentUrl = newLesson.video_url;
    await processVideoFile(
      file,
      currentUrl,
      (url) => setNewLesson((prev) => ({ ...prev, video_url: url })),
      "Lesson video uploaded successfully"
    );
  };

  const handleNewLessonVideoDrop = async (e) => {
    e.preventDefault();
    setNewLessonVideoDragOver(false);
    const file = e.dataTransfer.files[0];
    const currentUrl = newLesson.video_url;
    await processVideoFile(
      file,
      currentUrl,
      (url) => setNewLesson((prev) => ({ ...prev, video_url: url })),
      "Lesson video uploaded via drag and drop"
    );
  };

  const handleEditingLessonVideoUpload = async (e) => {
    const file = e.target.files[0];
    const currentUrl = editingLesson.video_url;
    await processVideoFile(
      file,
      currentUrl,
      (url) => setEditingLesson((prev) => ({ ...prev, video_url: url })),
      "Lesson video updated successfully"
    );
  };

  const handleEditingLessonVideoDrop = async (e) => {
    e.preventDefault();
    setEditingLessonVideoDragOver(false);
    const file = e.dataTransfer.files[0];
    const currentUrl = editingLesson.video_url;
    await processVideoFile(
      file,
      currentUrl,
      (url) => setEditingLesson((prev) => ({ ...prev, video_url: url })),
      "Lesson video updated via drag and drop"
    );
  };

  const handleCourseVideoDelete = async () => {
    const videoUrl = watch("video_url");
    if (!videoUrl) return;

    if (window.confirm("Are you sure you want to delete this course video?")) {
      try {
        await deleteFile(videoUrl);
        setValue("video_url", "", { shouldDirty: true });
        if (courseVideoInputRef.current) {
          courseVideoInputRef.current.value = "";
        }
        toast.success("Course video deleted successfully");
      } catch (error) {
        toast.error("Error deleting video: " + error.message);
      }
    }
  };

  const handleNewLessonVideoDelete = async () => {
    if (!newLesson.video_url) return;

    if (window.confirm("Are you sure you want to delete this lesson video?")) {
      try {
        await deleteFile(newLesson.video_url);
        setNewLesson((prev) => ({ ...prev, video_url: "" }));
        if (newLessonVideoInputRef.current) {
          newLessonVideoInputRef.current.value = "";
        }
        toast.success("Lesson video deleted successfully");
      } catch (error) {
        toast.error("Error deleting video: " + error.message);
      }
    }
  };

  const handleEditingLessonVideoDelete = async () => {
    if (!editingLesson.video_url) return;

    if (window.confirm("Are you sure you want to delete this lesson video?")) {
      try {
        await deleteFile(editingLesson.video_url);
        setEditingLesson((prev) => ({ ...prev, video_url: "" }));
        if (editingLessonVideoInputRef.current) {
          editingLessonVideoInputRef.current.value = "";
        }
        toast.success("Lesson video deleted successfully");
      } catch (error) {
        toast.error("Error deleting video: " + error.message);
      }
    }
  };

  // Handle requirements and learning outcomes
  const handleAddRequirement = () => {
    if (newRequirement.trim()) {
      setRequirements([...requirements, newRequirement.trim()]);
      setNewRequirement("");
    }
  };

  const handleRemoveRequirement = (index) => {
    setRequirements(requirements.filter((_, i) => i !== index));
  };

  const handleAddLearning = () => {
    if (newLearning.trim()) {
      setLearnings([...learnings, newLearning.trim()]);
      setNewLearning("");
    }
  };

  const handleRemoveLearning = (index) => {
    setLearnings(learnings.filter((_, i) => i !== index));
  };

  // Handle tag selection
  const handleTagToggle = (tagId) => {
    setSelectedTags((prev) =>
      prev.includes(tagId)
        ? prev.filter((id) => id !== tagId)
        : [...prev, tagId]
    );
  };

  const handleRemoveTag = (tagId) => {
    setSelectedTags((prev) => prev.filter((id) => id !== tagId));
  };

  // Section handlers
  const handleAddSection = () => {
    if (newSection.name.trim()) {
      const section = {
        id: Date.now().toString(),
        name: newSection.name.trim(),
        order: sections.length + 1,
        lessons: [],
      };
      setSections((prev) => [...prev, section]);
      setNewSection({ name: "", order: 0 });
    }
  };

  const handleEditSection = (sectionId) => {
    const section = sections.find((s) => s.id === sectionId);
    if (section) {
      setEditingSection({ ...section });
    }
  };

  const handleUpdateSection = () => {
    if (editingSection && editingSection.name.trim()) {
      setSections((prev) =>
        prev.map((s) => (s.id === editingSection.id ? editingSection : s))
      );
      setEditingSection(null);
    }
  };

  const handleRemoveSection = (sectionId) => {
    setSections((prev) => prev.filter((s) => s.id !== sectionId));
  };

  // Lesson handlers
  const handleAddLesson = (sectionId) => {
    if (newLesson.name.trim()) {
      const lesson = {
        id: Date.now().toString(),
        ...newLesson,
        name: newLesson.name.trim(),
        order:
          sections.find((s) => s.id === sectionId)?.lessons?.length + 1 || 1,
      };
      setSections((prev) =>
        prev.map((s) =>
          s.id === sectionId
            ? { ...s, lessons: [...(s.lessons || []), lesson] }
            : s
        )
      );
      setNewLesson({
        name: "",
        description: "",
        order: 0,
        content_type: "video",
        video_url: "",
        quiz: [], // Reset to empty array
      });
    }
  };

  const handleEditLesson = (sectionId, lessonId) => {
    const section = sections.find((s) => s.id === sectionId);
    const lesson = section?.lessons?.find((l) => l.id === lessonId);
    if (lesson) {
      // Ensure quiz is an array
      const lessonWithQuiz = {
        ...lesson,
        quiz: Array.isArray(lesson.quiz) ? lesson.quiz : [],
        sectionId,
      };
      setEditingLesson(lessonWithQuiz);
    }
  };

  const handleUpdateLesson = () => {
    if (editingLesson) {
      setSections((prev) =>
        prev.map((s) =>
          s.id === editingLesson.sectionId
            ? {
              ...s,
              lessons: s.lessons.map((l) =>
                l.id === editingLesson.id ? editingLesson : l
              ),
            }
            : s
        )
      );
      setEditingLesson(null);
    }
  };

  const handleRemoveLesson = (sectionId, lessonId) => {
    setSections((prev) =>
      prev.map((section) => {
        if (section.id === sectionId) {
          return {
            ...section,
            lessons: section.lessons.filter((lesson) => lesson.id !== lessonId),
          };
        }
        return section;
      })
    );
  };

  // Quiz management functions
  const addQuizQuestion = (lessonId, sectionId) => {
    const newQuestion = {
      id: Date.now().toString(),
      question: "",
      options: ["", "", "", ""],
      correct_ans_index: 0,
    };

    if (lessonId) {
      // Adding to existing lesson
      setSections((prev) =>
        prev.map((section) => {
          if (section.id === sectionId) {
            return {
              ...section,
              lessons: section.lessons.map((lesson) => {
                if (lesson.id === lessonId) {
                  return {
                    ...lesson,
                    quiz: [...(lesson.quiz || []), newQuestion],
                  };
                }
                return lesson;
              }),
            };
          }
          return section;
        })
      );
    } else {
      // Adding to new lesson
      setNewLesson((prev) => ({
        ...prev,
        quiz: [...prev.quiz, newQuestion],
      }));
    }
  };

  const updateQuizQuestion = (
    questionId,
    field,
    value,
    lessonId,
    sectionId
  ) => {
    if (lessonId) {
      // Updating existing lesson
      setSections((prev) =>
        prev.map((section) => {
          if (section.id === sectionId) {
            return {
              ...section,
              lessons: section.lessons.map((lesson) => {
                if (lesson.id === lessonId) {
                  return {
                    ...lesson,
                    quiz: lesson.quiz.map((q) =>
                      q.id === questionId ? { ...q, [field]: value } : q
                    ),
                  };
                }
                return lesson;
              }),
            };
          }
          return section;
        })
      );
    } else {
      // Updating new lesson
      setNewLesson((prev) => ({
        ...prev,
        quiz: prev.quiz.map((q) =>
          q.id === questionId ? { ...q, [field]: value } : q
        ),
      }));
    }
  };

  const removeQuizQuestion = (questionId, lessonId, sectionId) => {
    if (lessonId) {
      // Removing from existing lesson
      setSections((prev) =>
        prev.map((section) => {
          if (section.id === sectionId) {
            return {
              ...section,
              lessons: section.lessons.map((lesson) => {
                if (lesson.id === lessonId) {
                  return {
                    ...lesson,
                    quiz: lesson.quiz.filter((q) => q.id !== questionId),
                  };
                }
                return lesson;
              }),
            };
          }
          return section;
        })
      );
    } else {
      // Removing from new lesson
      setNewLesson((prev) => ({
        ...prev,
        quiz: prev.quiz.filter((q) => q.id !== questionId),
      }));
    }
  };

  const updateQuizOption = (
    questionId,
    optionIndex,
    value,
    lessonId,
    sectionId
  ) => {
    if (lessonId) {
      // Updating existing lesson
      setSections((prev) =>
        prev.map((section) => {
          if (section.id === sectionId) {
            return {
              ...section,
              lessons: section.lessons.map((lesson) => {
                if (lesson.id === lessonId) {
                  return {
                    ...lesson,
                    quiz: lesson.quiz.map((q) => {
                      if (q.id === questionId) {
                        const newOptions = [...q.options];
                        newOptions[optionIndex] = value;
                        return { ...q, options: newOptions };
                      }
                      return q;
                    }),
                  };
                }
                return lesson;
              }),
            };
          }
          return section;
        })
      );
    } else {
      // Updating new lesson
      setNewLesson((prev) => ({
        ...prev,
        quiz: prev.quiz.map((q) => {
          if (q.id === questionId) {
            const newOptions = [...q.options];
            newOptions[optionIndex] = value;
            return { ...q, options: newOptions };
          }
          return q;
        }),
      }));
    }
  };

  // Book management functions
  const handleAddBook = (book) => {
    if (!selectedBooks.find((b) => b.id === book.id)) {
      const newSelectedBooks = [...selectedBooks, book];
      setSelectedBooks(newSelectedBooks);

      // Automatically update course price based on book value if enabled
      if (autoPriceEnabled) {
        updateCoursePriceForBooks(newSelectedBooks);
      }
    }
  };

  const handleRemoveBook = (bookId) => {
    const newSelectedBooks = selectedBooks.filter((b) => b.id !== bookId);
    setSelectedBooks(newSelectedBooks);

    // Only recalculate when there are books; avoid resetting base price when none
    if (autoPriceEnabled && newSelectedBooks.length > 0) {
      updateCoursePriceForBooks(newSelectedBooks);
    }
  };

  // Function to automatically update course price based on book values
  const updateCoursePriceForBooks = (books) => {
    // Base course price typed by user or existing value
    const basePrice = parseFloat(watchedPrice) || 0;

    // Calculate total book value
    const totalBookValue = Array.isArray(books)
      ? books.reduce(
        (total, book) => total + (parseFloat(book?.amount) || 0),
        0
      )
      : 0;

    const combinedPrice = basePrice + totalBookValue;

    // Set price status from combined total
    if (Array.isArray(books) && books.length > 0) {
      // Only adjust price_status when books are involved
      setValue("price_status", combinedPrice > 0 ? "paid" : "free");
    }

    // Update visible price to combined (course + books)
    setValue("pricee", combinedPrice.toString());

    toast.success(
      `Course price updated to ₵${combinedPrice.toFixed(2)} (₵${basePrice.toFixed(
        2
      )} course + ₵${totalBookValue.toFixed(2)} books)`
    );
  };

  const handleBookSearch = (searchTerm) => {
    setBookSearchTerm(searchTerm);
  };

  const getFilteredBooks = () => {
    if (!bookSearchTerm.trim()) return books;
    return searchBooks(bookSearchTerm);
  };

  useEffect(() => {
    if (isOpen && course && mode !== "add") {
      // Populate form with course data
      Object.keys(course).forEach((key) => {
        if (key !== "id") {
          setValue(key, course[key]);
        }
      });

      // Set image preview
      if (course.image_url) {
        setImagePreview(course.image_url);
      }

      // Set selected author (handle both old and new structure)
      if (course.author_id) {
        setSelectedAuthor(course.author_id);
      } else if (course.author?.id) {
        setSelectedAuthor(course.author.id);
      }

      // Set selected students (if stored as array of IDs)
      if (course.enrolled_students && Array.isArray(course.enrolled_students)) {
        setSelectedStudents(course.enrolled_students);
      }

      // Set requirements and learnings from meta object
      if (
        course.meta?.requirements &&
        Array.isArray(course.meta.requirements)
      ) {
        setRequirements(course.meta.requirements);
      }
      if (course.meta?.learnings && Array.isArray(course.meta.learnings)) {
        setLearnings(course.meta.learnings);
      }

      // Set selected tags
      if (course.tag_ids && Array.isArray(course.tag_ids)) {
        setSelectedTags(course.tag_ids);
      }

      // Set selected program
      if (course.program) {
        setSelectedProgram(course.program);
        // Add custom program to options if it's not already there
        if (!programOptions.includes(course.program)) {
          setProgramOptions((prev) => [...prev, course.program]);
        }
      } else {
        // Handle existing courses without program field
        setSelectedProgram("");
      }

      // Set selected category
      if (course.cat_id) {
        setSelectedCategory(course.cat_id);
        // Add custom category to options if it's not already there
        if (
          course.program &&
          !getCategoryOptions(course.program).includes(course.cat_id)
        ) {
          setCustomCategories((prev) => ({
            ...prev,
            [course.program]: [...(prev[course.program] || []), course.cat_id],
          }));
        }
      } else {
        // Handle existing courses without category field
        setSelectedCategory("");
      }

      // Set sections
      if (course.sections && Array.isArray(course.sections)) {
        // Ensure quiz fields are arrays in all lessons
        const sectionsWithQuizArrays = course.sections.map((section) => ({
          ...section,
          lessons:
            section.lessons?.map((lesson) => ({
              ...lesson,
              quiz: Array.isArray(lesson.quiz) ? lesson.quiz : [],
            })) || [],
        }));
        setSections(sectionsWithQuizArrays);
      }

      // Set selected books
      if (course.books && Array.isArray(course.books)) {
        setSelectedBooks(course.books);
        // If auto-pricing is enabled and there are books, update using combined price
        if (autoPriceEnabled && course.books.length > 0) {
          updateCoursePriceForBooks(course.books);
        }
      }
    } else if (isOpen && mode === "add") {
      // Only call reset if the form is properly initialized
      if (reset && typeof reset === "function" && formReady) {
        try {
          // reset();
        } catch (error) {
          console.warn("Error calling reset function:", error);
        }
      }
      setImagePreview("");
      // Automatically set the current user as the instructor when creating a new course
      if (currentUser) {
        setSelectedAuthor(currentUser.uid);
      } else {
        setSelectedAuthor("");
      }
      setSelectedStudents([]);
      setRequirements([]);
      setLearnings([]);
      setNewRequirement("");
      setNewLearning("");
      setSelectedTags([]);
      setSelectedProgram("");
      setSelectedCategory("");
      setSections([]);
      setNewSection({ name: "", order: 0 });
      setNewLesson({
        name: "",
        description: "",
        order: 0,
        content_type: "video",
        video_url: "",
        quiz: [], // Reset to empty array
      });
      setSelectedBooks([]);
      setBookSearchTerm("");
      setShowBookModal(false);
      setEditingSection(null);
      setEditingLesson(null);
      setEditorReady(false);
      setValue("status", "draft");
      setValue("price_status", "free");
      setValue("pricee", "0");
      setValue("rating", 0);
      setValue("students", 0);
      setValue("lessons_count", 0);
      setValue("featured", false);
      setValue("created_at", new Date().toISOString().slice(0, 16));
    }
  }, [isOpen, course, mode, setValue, formReady]);

  // Reset category when program changes
  useEffect(() => {
    if (selectedProgram) {
      setSelectedCategory("");
    }
  }, [selectedProgram]);

  // Cleanup editor on unmount
  useEffect(() => {
    return () => {
      try {
        const editor = editorRef.current;
        if (
          editor &&
          typeof editor === "object" &&
          typeof editor.destroy === "function"
        ) {
          editor.destroy();
          editorRef.current = null; // Clear the reference
        }
      } catch (error) {
        console.warn("Error destroying editor:", error);
        editorRef.current = null; // Clear the reference even on error
      }
    };
  }, []);

  const onSubmit = async (data) => {
    // Validate that an instructor is selected
    if (mode === "add" && !selectedAuthor) {
      alert("Please select an instructor for this course before saving.");
      return;
    }

    // Validate that program is selected
    if (!selectedProgram) {
      alert("Please select a program for the course.");
      return;
    }

    // Validate that category is selected
    if (!selectedCategory) {
      alert("Please select a category for the course.");
      return;
    }

    setLoading(true);
    try {
      // Format data for Firestore
      const courseData = {
        ...data,
        pricee: data.pricee || "0",
        rating: parseInt(data.rating) || 0,
        lessons_count: sections.reduce(
          (total, section) => total + (section.lessons?.length || 0),
          0
        ),
        created_at: mode === "add" ? new Date() : course.created_at,
        updated_at: new Date(),
        // Handle meta object
        meta: {
          duration: data.duration || "0",
          summary: data.summary || "",
          description: data.description || "",
          language: data.language || "English",
          requirements: requirements,
          learnings: learnings,
        },
        // Store full author object if author is selected
        author_id: selectedAuthor || data.author_id || "",
        author: selectedAuthor
          ? (() => {
            const authorData = getUserById(selectedAuthor);
            return authorData
              ? {
                id: authorData.id,
                name: authorData.name || "Unknown Author",
                image_url: authorData.image_url || null,
                status: data.author_status || authorData.status || "Online",
                zoomId: data.zoom_id || authorData.zoomId || "",
              }
              : {
                id: selectedAuthor,
                name: "Unknown Author",
                image_url: null,
                status: data.author_status || "Online",
                zoomId: data.zoom_id || "",
              };
          })()
          : mode === "edit" && course.author
            ? course.author
            : null,
        // Handle enrolled students (store only IDs)
        enrolled_students: selectedStudents,
        students: selectedStudents.length, // Update student count based on enrolled students
        // Handle tag IDs
        tag_ids: selectedTags,
        // Handle program and category
        program: selectedProgram,
        cat_id: selectedCategory,
        // Handle sections and lessons
        sections: sections,
        // Handle books
        books: selectedBooks,
        // Handle featured status
        featured: data.featured || false,
      };

      // For new courses, ensure both userId and author are set
      if (mode === "add" && currentUser) {
        courseData.userId = currentUser.uid;
        // Ensure the current user is set as the author when creating a new course
        if (selectedAuthor === currentUser.uid) {
          // Current user is selected as instructor, ensure author_id is set
          courseData.author_id = currentUser.uid;
          // The author object will be set by the existing logic above
        } else if (!selectedAuthor) {
          // Fallback: if somehow no author is selected, set current user
          courseData.author_id = currentUser.uid;
          courseData.author =
            currentUser.email || currentUser.displayName || currentUser.uid;
        }
      }

      await onSave(courseData, mode === "edit" ? course.id : null);
      onClose();
    } catch (error) {
      console.error("Error saving course:", error);
      alert("Error saving course. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const getStatusColor = (status) => {
    switch (status) {
      case "live":
        return "text-green-700 bg-green-50 ring-green-600/20";
      case "draft":
        return "text-yellow-700 bg-yellow-50 ring-yellow-600/20";
      case "archived":
        return "text-gray-700 bg-gray-50 ring-gray-600/20";
      default:
        return "text-gray-700 bg-gray-50 ring-gray-600/20";
    }
  };

  const getPriceStatusColor = (priceStatus) => {
    switch (priceStatus) {
      case "free":
        return "text-green-700 bg-green-50 ring-green-600/20";
      case "paid":
        return "text-blue-700 bg-blue-50 ring-blue-600/20";
      case "premium":
        return "text-purple-700 bg-purple-50 ring-purple-600/20";
      default:
        return "text-gray-700 bg-gray-50 ring-gray-600/20";
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        <div
          className="fixed inset-0 bg-gradient-to-br from-gray-900/80 to-black/80 backdrop-blur-sm transition-opacity"
          onClick={onClose}
        />

        <div className="relative w-full max-w-5xl transform rounded-3xl bg-white shadow-2xl transition-all border border-gray-100 overflow-hidden">
          {/* Header */}
          <div className="bg-white border-b border-gray-200 px-8 py-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className="p-3 bg-blue-100 rounded-xl">
                  <AcademicCapIcon className="h-8 w-8 text-blue-600" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-gray-900">
                    {mode === "add" && (
                      <>
                        <SparklesIcon className="h-6 w-6 inline mr-2 text-blue-500" />
                        Create New Course
                      </>
                    )}
                    {mode === "edit" && (
                      <>
                        <PencilIcon className="h-6 w-6 inline mr-2 text-blue-500" />
                        Edit Course
                      </>
                    )}
                    {mode === "view" && (
                      <>
                        <EyeIcon className="h-6 w-6 inline mr-2 text-blue-500" />
                        Course Details
                      </>
                    )}
                  </h3>
                  <p className="text-gray-600 mt-1">
                    {mode === "add" &&
                      "Build an amazing learning experience for your students"}
                    {mode === "edit" &&
                      "Update course information and settings"}
                    {mode === "view" && "View comprehensive course information"}
                  </p>
                </div>
              </div>

              <button
                onClick={onClose}
                className="p-3 rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors duration-200"
                title="Close"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            {/* Progress indicator for add mode */}
            {mode === "add" && (
              <div className="mt-4 flex items-center space-x-2 text-sm text-gray-600">
                <InformationCircleIcon className="h-4 w-4 text-blue-500" />
                <span>Fill in the details below to create your course</span>
              </div>
            )}
          </div>

          {/* Content */}
          <form
            onSubmit={handleSubmit(onSubmit)}
            className="max-h-[80vh] overflow-y-auto"
          >
            <div className="px-8 py-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Left Column - Basic Info */}
                <div className="space-y-8">
                  {/* Basic Information Section */}
                  <div className="space-y-6">
                    <div className="border-b border-gray-200 pb-4">
                      <h4 className="text-xl font-semibold text-gray-900">
                        Basic Information
                      </h4>
                      <p className="text-sm text-gray-600 mt-1">
                        Essential course details and settings
                      </p>
                    </div>

                    {/* Course Name */}
                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-gray-700">
                        Course Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        {...register("name", {
                          required: "Course name is required",
                        })}
                        disabled={mode === "view"}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-50 disabled:text-gray-500 transition-colors duration-200 text-base"
                        placeholder="Enter course name..."
                      />
                      {errors.name && (
                        <p className="text-sm text-red-600 flex items-center mt-1">
                          <ExclamationCircleIcon className="h-4 w-4 mr-1" />
                          {errors.name.message}
                        </p>
                      )}
                    </div>

                    {/* Program */}
                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-gray-700">
                        Program <span className="text-red-500">*</span>
                      </label>
                      <p className="text-xs text-gray-500">
                        Select the program this course belongs to. You can also
                        add custom programs if needed. Custom programs can be
                        edited or deleted later.
                      </p>
                      {mode === "view" ? (
                        <div className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg text-gray-700">
                          {selectedProgram ? (
                            <div className="flex items-center justify-between">
                              <span>{selectedProgram}</span>
                              <div className="flex items-center gap-2">
                                {![
                                  "ICAG",
                                  "CITG",
                                  "Corporate Training",
                                  "Digital Marketing",
                                ].includes(selectedProgram) && (
                                    <span className="text-xs text-blue-600 bg-blue-100 px-2 py-1 rounded-full">
                                      Custom
                                    </span>
                                  )}
                                {isProgramEditable(selectedProgram) && (
                                  <>
                                    <button
                                      type="button"
                                      onClick={() => {
                                        setEditingProgram(selectedProgram);
                                        setEditProgram(selectedProgram);
                                        setShowEditProgramModal(true);
                                      }}
                                      className="text-xs text-amber-600 hover:text-amber-700"
                                      title="Edit this custom program"
                                    >
                                      ✏️
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => {
                                        setDeletingProgram(selectedProgram);
                                        setShowDeleteProgramModal(true);
                                      }}
                                      className="text-xs text-red-600 hover:text-red-700"
                                      title="Delete this custom program"
                                    >
                                      🗑️
                                    </button>
                                  </>
                                )}
                              </div>
                            </div>
                          ) : (
                            "No program selected"
                          )}
                        </div>
                      ) : (
                        <div className="space-y-2">
                          <select
                            value={selectedProgram}
                            onChange={(e) => setSelectedProgram(e.target.value)}
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200 appearance-none bg-white"
                          >
                            <option value="">Select a program</option>
                            {programOptions.map((program) => (
                              <option key={program} value={program}>
                                {program}
                              </option>
                            ))}
                          </select>
                          <div className="flex gap-2">
                            <button
                              type="button"
                              onClick={() => setShowCustomProgramModal(true)}
                              className="flex-1 px-3 py-2 text-sm text-blue-600 border border-blue-300 rounded-lg hover:bg-blue-50 hover:border-blue-400 transition-colors duration-200 flex items-center justify-center"
                            >
                              <span className="mr-2">+</span>
                              Add Custom Program
                            </button>
                            {selectedProgram &&
                              isProgramEditable(selectedProgram) && (
                                <>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setEditingProgram(selectedProgram);
                                      setEditProgram(selectedProgram);
                                      setShowEditProgramModal(true);
                                    }}
                                    className="px-3 py-2 text-sm text-amber-600 border border-amber-300 rounded-lg hover:bg-amber-50 hover:border-amber-400 transition-colors duration-200 flex items-center justify-center"
                                    title="Edit this custom program"
                                  >
                                    <span className="mr-2">✏️</span>
                                    Edit
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setDeletingProgram(selectedProgram);
                                      setShowDeleteProgramModal(true);
                                    }}
                                    className="px-3 py-2 text-sm text-red-600 border border-red-300 rounded-lg hover:bg-red-50 hover:border-red-400 transition-colors duration-200 flex items-center justify-center"
                                    title="Delete this custom program"
                                  >
                                    <span className="mr-2">🗑️</span>
                                    Delete
                                  </button>
                                </>
                              )}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Category */}
                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-gray-700">
                        Category <span className="text-red-500">*</span>
                      </label>
                      <p className="text-xs text-gray-500">
                        Select the specific category within the chosen program.
                        You can also add custom categories if needed. Custom
                        categories can be edited or deleted later.
                      </p>
                      {mode === "view" ? (
                        <div className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg text-gray-700">
                          {selectedCategory ? (
                            <div className="flex items-center justify-between">
                              <span>{selectedCategory}</span>
                              <div className="flex items-center gap-2">
                                {!getCategoryOptions(selectedProgram).includes(
                                  selectedCategory
                                ) && (
                                    <span className="text-xs text-blue-600 bg-blue-100 px-2 py-1 rounded-full">
                                      Custom
                                    </span>
                                  )}
                                {isCategoryEditable(selectedCategory) && (
                                  <>
                                    <button
                                      type="button"
                                      onClick={() => {
                                        setEditingCategory(selectedCategory);
                                        setEditCategory(selectedCategory);
                                        setShowEditCategoryModal(true);
                                      }}
                                      className="text-xs text-amber-600 hover:text-amber-700"
                                      title="Edit this custom category"
                                    >
                                      ✏️
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => {
                                        setDeletingCategory(selectedCategory);
                                        setShowDeleteCategoryModal(true);
                                      }}
                                      className="text-xs text-red-600 hover:text-red-700"
                                      title="Delete this custom category"
                                    >
                                      🗑️
                                    </button>
                                  </>
                                )}
                              </div>
                            </div>
                          ) : (
                            "No category selected"
                          )}
                        </div>
                      ) : (
                        <>
                          <select
                            value={selectedCategory}
                            onChange={(e) =>
                              setSelectedCategory(e.target.value)
                            }
                            disabled={!selectedProgram}
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200 appearance-none bg-white disabled:bg-gray-100 disabled:cursor-not-allowed"
                          >
                            <option value="">
                              {!selectedProgram
                                ? "Please select a program first"
                                : "Select a category"}
                            </option>
                            {selectedProgram &&
                              getCategoryOptions(selectedProgram).map(
                                (category) => (
                                  <option key={category} value={category}>
                                    {category}
                                  </option>
                                )
                              )}
                          </select>
                          {!selectedProgram && (
                            <p className="text-xs text-amber-600 mt-1">
                              ⚠️ Please select a program first to choose a
                              category
                            </p>
                          )}
                          {selectedProgram && (
                            <div className="flex gap-2">
                              <button
                                type="button"
                                onClick={() => setShowCustomCategoryModal(true)}
                                className="flex-1 px-3 py-2 text-sm text-green-600 border border-green-300 rounded-lg hover:bg-green-50 hover:border-green-400 transition-colors duration-200 flex items-center justify-center"
                              >
                                <span className="mr-2">+</span>
                                Add Custom Category
                              </button>
                              {selectedCategory &&
                                isCategoryEditable(selectedCategory) && (
                                  <>
                                    <button
                                      type="button"
                                      onClick={() => {
                                        setEditingCategory(selectedCategory);
                                        setEditCategory(selectedCategory);
                                        setShowEditCategoryModal(true);
                                      }}
                                      className="px-3 py-2 text-sm text-amber-600 border border-amber-300 rounded-lg hover:bg-amber-50 hover:border-amber-400 transition-colors duration-200 flex items-center justify-center"
                                      title="Edit this custom category"
                                    >
                                      <span className="mr-2">✏️</span>
                                      Edit
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => {
                                        setDeletingCategory(selectedCategory);
                                        setShowDeleteCategoryModal(true);
                                      }}
                                      className="px-3 py-2 text-sm text-red-600 border border-red-300 rounded-lg hover:bg-red-50 hover:border-red-400 transition-colors duration-200 flex items-center justify-center"
                                      title="Delete this custom category"
                                    >
                                      <span className="mr-2">🗑️</span>
                                      Delete
                                    </button>
                                  </>
                                )}
                            </div>
                          )}
                        </>
                      )}
                    </div>

                    {/* Price and Status Row */}
                    <div className="space-y-2">
                      {/* Auto-price update notification */}
                      {selectedBooks.length > 0 && (
                        <div
                          className={`p-3 border rounded-lg ${autoPriceEnabled
                            ? "bg-blue-50 border-blue-200"
                            : "bg-yellow-50 border-yellow-200"
                            }`}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center">
                              <div className="flex-shrink-0">
                                <svg
                                  className={`h-5 w-5 ${autoPriceEnabled
                                    ? "text-blue-400"
                                    : "text-yellow-400"
                                    }`}
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                                  />
                                </svg>
                              </div>
                              <div className="ml-3">
                                <p
                                  className={`text-sm ${autoPriceEnabled
                                    ? "text-blue-800"
                                    : "text-yellow-800"
                                    }`}
                                >
                                  <strong>Auto-Price Update:</strong>{" "}
                                  {autoPriceEnabled
                                    ? "Course price is automatically calculated based on the total value of assigned books."
                                    : "Auto-price update is disabled. Use the refresh button to manually update price based on book values."}
                                </p>
                                <p
                                  className={`text-xs mt-1 ${autoPriceEnabled
                                    ? "text-blue-600"
                                    : "text-yellow-600"
                                    }`}
                                >
                                  Total book value: ₵
                                  {selectedBooks
                                    .reduce(
                                      (total, book) =>
                                        total + (parseFloat(book.amount) || 0),
                                      0
                                    )
                                    .toFixed(2)}
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center">
                              <label className="flex items-center text-sm text-blue-700">
                                <input
                                  type="checkbox"
                                  checked={autoPriceEnabled}
                                  onChange={(e) =>
                                    setAutoPriceEnabled(e.target.checked)
                                  }
                                  className="mr-2 rounded border-blue-300 text-blue-600 focus:ring-blue-500"
                                />
                                Auto-update price
                              </label>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="grid grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <label className="block text-sm font-medium text-gray-700">
                          Price Status
                        </label>
                        <select
                          {...register("price_status")}
                          disabled={mode === "view"}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-50 disabled:text-gray-500 transition-colors duration-200 appearance-none bg-white"
                        >
                          <option value="free">Free</option>
                          <option value="paid">Paid</option>
                          <option value="premium">Premium</option>
                        </select>
                      </div>

                      <div className="space-y-2">
                        <label className="block text-sm font-medium text-gray-700">
                          Price (GHS ₵)
                        </label>
                        <div className="flex gap-2">
                          <input
                            type="number"
                            {...register("pricee")}
                            disabled={
                              mode === "view" || watchedPriceStatus === "free"
                            }
                            className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-50 disabled:text-gray-500 transition-colors duration-200"
                            placeholder="0.00"
                            min="0"
                            step="0.01"
                          />
                          {!autoPriceEnabled && selectedBooks.length > 0 && (
                            <button
                              type="button"
                              onClick={() =>
                                updateCoursePriceForBooks(selectedBooks)
                              }
                              className="px-3 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 transition-colors duration-200"
                              title="Update price based on book values"
                            >
                              <svg
                                className="h-4 w-4"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                                />
                              </svg>
                            </button>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Course Status and Featured */}
                    <div className="grid grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <label className="block text-sm font-medium text-gray-700">
                          Course Status
                        </label>
                        <select
                          {...register("status")}
                          disabled={mode === "view"}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-50 disabled:text-gray-500 transition-colors duration-200 appearance-none bg-white"
                        >
                          <option value="draft">Draft</option>
                          <option value="live">Live</option>
                          <option value="archived">Archived</option>
                        </select>
                      </div>

                      <div className="space-y-2">
                        <label className="block text-sm font-medium text-gray-700">
                          Featured Course
                        </label>
                        <div className="flex items-center space-x-3">
                          <label className="flex items-center">
                            <input
                              type="checkbox"
                              {...register("featured")}
                              disabled={mode === "view"}
                              className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                            />
                            <span className="ml-2 text-sm text-gray-700">
                              Mark as featured
                            </span>
                          </label>
                          <div className="flex items-center text-xs text-gray-500">
                            <svg
                              className="h-4 w-4 text-yellow-500 mr-1"
                              fill="currentColor"
                              viewBox="0 0 20 20"
                            >
                              <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                            </svg>
                            Featured courses appear prominently
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Statistics Row */}
                    <div className="grid grid-cols-3 gap-6">
                      <div className="space-y-2">
                        <label className="block text-sm font-medium text-gray-700">
                          Students
                        </label>
                        <input
                          type="number"
                          {...register("students")}
                          disabled={mode === "view"}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-50 disabled:text-gray-500 transition-colors duration-200"
                          min="0"
                          placeholder="0"
                        />
                      </div>

                      <div className="space-y-2">
                        <label className="block text-sm font-medium text-gray-700">
                          Lessons
                        </label>
                        <input
                          type="number"
                          {...register("lessons_count")}
                          disabled={mode === "view"}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-50 disabled:text-gray-500 transition-colors duration-200"
                          min="0"
                          placeholder="0"
                        />
                      </div>

                      <div className="space-y-2">
                        <label className="block text-sm font-medium text-gray-700">
                          Rating
                        </label>
                        <input
                          type="number"
                          {...register("rating")}
                          disabled={mode === "view"}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-50 disabled:text-gray-500 transition-colors duration-200"
                          min="0"
                          max="5"
                          step="0.1"
                          placeholder="5.0"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Right Column - Media & Details */}
                <div className="space-y-8">
                  {/* Media & Advanced Section */}
                  <div className="space-y-6">
                    <div className="border-b border-gray-200 pb-4">
                      <h4 className="text-xl font-semibold text-gray-900">
                        Media & Advanced
                      </h4>
                      <p className="text-sm text-gray-600 mt-1">
                        Course media and additional settings
                      </p>
                    </div>

                    {/* Course Image */}
                    <div className="space-y-3">
                      <label className="block text-sm font-medium text-gray-700">
                        Course Image
                      </label>
                      <div
                        className={`relative border-2 border-dashed rounded-lg p-6 transition-all duration-200 ${dragOver
                          ? "border-blue-500 bg-blue-50"
                          : "border-gray-300 hover:border-gray-400 hover:bg-gray-50"
                          }`}
                        onDragOver={handleDragOver}
                        onDragLeave={handleDragLeave}
                        onDrop={handleDrop}
                      >
                        {imagePreview ? (
                          <div className="relative group">
                            <img
                              src={imagePreview}
                              alt="Course preview"
                              className="w-full h-40 object-cover rounded-lg"
                            />
                            <div className="absolute inset-0 bg-black/20 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200"></div>
                            {mode !== "view" && (
                              <button
                                type="button"
                                onClick={() => {
                                  setImagePreview("");
                                  setValue("image_url", "");
                                }}
                                className="absolute top-3 right-3 bg-red-500 text-white rounded-full p-2 hover:bg-red-600 opacity-0 group-hover:opacity-100 transition-all duration-200"
                                title="Remove image"
                              >
                                <XMarkIcon className="h-4 w-4" />
                              </button>
                            )}
                          </div>
                        ) : (
                          <div className="text-center">
                            <div className="p-4 bg-gray-100 rounded-full w-20 h-20 mx-auto mb-4 flex items-center justify-center">
                              <CloudArrowUpIcon className="h-10 w-10 text-gray-500" />
                            </div>
                            <div className="space-y-2">
                              <p className="text-lg font-medium text-gray-700">
                                {mode === "view"
                                  ? "No image uploaded"
                                  : "Upload Course Image"}
                              </p>
                              <p className="text-sm text-gray-500">
                                {mode === "view"
                                  ? ""
                                  : "Drag and drop your image here, or click to browse"}
                              </p>
                              <p className="text-xs text-gray-400">
                                {mode === "view"
                                  ? ""
                                  : "PNG, JPG, GIF up to 5MB"}
                              </p>
                              {mode !== "view" && (
                                <input
                                  type="file"
                                  accept="image/*"
                                  onChange={async (e) => {
                                    const file = e.target.files[0];
                                    if (file) {
                                      try {
                                        const imageUrl =
                                          await handleImageUpload(file);
                                        setImagePreview(imageUrl);
                                        setValue("image_url", imageUrl);
                                      } catch (error) {
                                        alert(
                                          "Error uploading image: " + error
                                        );
                                      }
                                    }
                                  }}
                                  className="sr-only"
                                  id="course-image"
                                />
                              )}
                            </div>
                            {mode !== "view" && (
                              <label
                                htmlFor="course-image"
                                className="mt-4 inline-flex cursor-pointer items-center rounded-lg bg-blue-600 px-6 py-3 text-sm font-medium text-white hover:bg-blue-700 transition-colors duration-200"
                              >
                                <PhotoIcon className="h-4 w-4 mr-2" />
                                Choose Image
                              </label>
                            )}
                          </div>
                        )}
                      </div>
                      <input type="hidden" {...register("image_url")} />
                    </div>

                    {/* Video URL Improvement */}
                    <div className="space-y-4 pt-2">
                      <label className="block text-sm font-semibold text-gray-700 flex items-center">
                        <VideoCameraIcon className="h-4 w-4 mr-2 text-blue-500" />
                        Course Promotional Video
                      </label>

                      <div
                        onDragOver={(e) => {
                          e.preventDefault();
                          setCourseVideoDragOver(true);
                        }}
                        onDragLeave={(e) => {
                          e.preventDefault();
                          setCourseVideoDragOver(false);
                        }}
                        onDrop={handleCourseVideoDrop}
                        className={`relative group p-4 border-2 border-dashed rounded-xl transition-all duration-300 ${videoUploading || courseVideoDragOver
                            ? "border-blue-400 bg-blue-50 shadow-inner"
                            : "border-gray-200 bg-gray-50/50 hover:border-blue-400 hover:bg-white hover:shadow-md"
                          }`}
                      >
                        <div className="flex flex-col gap-3">
                          <div className="flex items-center gap-3">
                            <div className="p-2 bg-white rounded-lg shadow-sm border border-gray-100 group-hover:border-blue-100 transition-colors">
                              <VideoCameraIcon
                                className={`h-6 w-6 ${videoUploading
                                  ? "text-blue-400 animate-pulse"
                                  : "text-blue-600"
                                  }`}
                              />
                            </div>
                            <div className="flex-1 min-w-0">
                              <input
                                type="url"
                                {...register("video_url")}
                                disabled={mode === "view" || videoUploading}
                                className="w-full bg-transparent border-none p-0 text-sm focus:ring-0 placeholder:text-gray-400 text-gray-700 font-medium"
                                placeholder="Paste a link (YouTube/Vimeo) or drag and drop a video..."
                              />
                            </div>
                            {mode !== "view" && !videoUploading && (
                              <div className="flex items-center gap-2">
                                {watch("video_url") && (
                                  <button
                                    type="button"
                                    onClick={handleCourseVideoDelete}
                                    className="p-1.5 text-red-100 font-semibold bg-red-600 border border-red-600 rounded-lg hover:bg-red-700 hover:border-red-700 transition-all duration-200 shadow-sm flex items-center justify-center active:scale-95"
                                    title="Delete Video"
                                  >
                                    <TrashIcon className="h-3.5 w-3.5" />
                                  </button>
                                )}
                                <button
                                  type="button"
                                  onClick={() =>
                                    courseVideoInputRef.current?.click()
                                  }
                                  className="px-3 py-1.5 text-xs font-semibold text-blue-600 bg-white border border-blue-200 rounded-lg hover:bg-blue-600 hover:text-white hover:border-blue-600 transition-all duration-200 shadow-sm flex items-center gap-1.5 active:scale-95"
                                >
                                  <CloudArrowUpIcon className="h-3.5 w-3.5" />
                                  Browse
                                </button>
                              </div>
                            )}
                          </div>

                          {videoUploading && (
                            <div className="space-y-1.5 animate-in fade-in duration-300">
                              <div className="flex justify-between text-[10px] text-blue-600 font-bold uppercase tracking-wider">
                                <span>Uploading course video...</span>
                                <span>{Math.round(videoUploadProgress)}%</span>
                              </div>
                              <div className="w-full bg-blue-100 rounded-full h-1.5 overflow-hidden shadow-inner">
                                <div
                                  className="bg-blue-600 h-full rounded-full transition-all duration-500 ease-out shadow-[0_0_10px_rgba(37,99,235,0.5)]"
                                  style={{ width: `${videoUploadProgress}%` }}
                                ></div>
                              </div>
                            </div>
                          )}
                        </div>

                        <input
                          type="file"
                          ref={courseVideoInputRef}
                          onChange={handleCourseVideoUpload}
                          accept="video/*"
                          className="hidden"
                        />
                      </div>
                    </div>

                    {/* Duration and Language */}
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-3">
                        <label className="text-sm font-semibold text-gray-700 flex items-center">
                          <ClockIcon className="h-4 w-4 mr-2 text-blue-500" />
                          Duration (minutes)
                        </label>
                        <div className="relative group">
                          <input
                            type="number"
                            {...register("duration")}
                            disabled={mode === "view"}
                            className="w-full rounded-lg border-2 border-gray-200 bg-white shadow-sm focus:border-blue-500 focus:ring-4 focus:ring-blue-100 disabled:bg-gray-50 disabled:text-gray-500 disabled:border-gray-200 transition-all duration-300 group-hover:border-gray-300 group-hover:shadow-md focus:shadow-lg"
                            placeholder="90"
                            min="0"
                          />
                        </div>
                      </div>
                      <div className="space-y-3">
                        <label className="text-sm font-semibold text-gray-700 flex items-center">
                          <AcademicCapIcon className="h-4 w-4 mr-2 text-green-500" />
                          Language
                        </label>
                        <div className="relative group">
                          <input
                            type="text"
                            {...register("language")}
                            disabled={mode === "view"}
                            className="w-full rounded-lg border-2 border-gray-200 bg-white shadow-sm focus:border-green-500 focus:ring-4 focus:ring-green-100 disabled:bg-gray-50 disabled:text-gray-500 disabled:border-gray-200 transition-all duration-300 group-hover:border-gray-300 group-hover:shadow-md focus:shadow-lg"
                            placeholder="English"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Tags */}
                    <div className="space-y-3">
                      <label className="block text-sm font-medium text-gray-700">
                        Course Tags
                      </label>
                      {mode === "view" ? (
                        <div className="space-y-2">
                          {course?.tag_ids?.length > 0 ? (
                            <div className="flex flex-wrap gap-2">
                              {getTagNamesByIds(course.tag_ids).map(
                                (tagName, index) => (
                                  <span
                                    key={index}
                                    className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-purple-100 text-purple-800 border border-purple-200"
                                  >
                                    <TagIcon className="w-3 h-3 mr-1" />
                                    {tagName}
                                  </span>
                                )
                              )}
                            </div>
                          ) : (
                            <p className="text-gray-500 text-sm">
                              No tags assigned
                            </p>
                          )}
                        </div>
                      ) : (
                        <div className="space-y-3">
                          {/* Available Tags */}
                          <div>
                            <p className="text-xs text-gray-600 mb-2">
                              Select from available tags:
                            </p>
                            {tagsLoading ? (
                              <p className="text-sm text-gray-500">
                                Loading tags...
                              </p>
                            ) : tags.length > 0 ? (
                              <div className="flex flex-wrap gap-2">
                                {tags.map((tag) => (
                                  <button
                                    key={tag.id}
                                    type="button"
                                    onClick={() => handleTagToggle(tag.id)}
                                    className={`inline-flex items-center px-3 py-2 rounded-lg text-sm font-medium transition-colors duration-200 ${selectedTags.includes(tag.id)
                                      ? "bg-purple-600 text-white border border-purple-600"
                                      : "bg-gray-100 text-gray-700 border border-gray-300 hover:bg-gray-200"
                                      }`}
                                  >
                                    <TagIcon className="w-3 h-3 mr-1" />
                                    {tag.name}
                                  </button>
                                ))}
                              </div>
                            ) : (
                              <p className="text-sm text-gray-500">
                                No tags available
                              </p>
                            )}
                          </div>

                          {/* Selected Tags */}
                          {selectedTags.length > 0 && (
                            <div>
                              <p className="text-xs text-gray-600 mb-2">
                                Selected tags:
                              </p>
                              <div className="flex flex-wrap gap-2">
                                {selectedTags.map((tagId) => {
                                  const tag = tags.find((t) => t.id === tagId);
                                  return tag ? (
                                    <div
                                      key={tagId}
                                      className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-purple-100 text-purple-800 border border-purple-200"
                                    >
                                      <TagIcon className="w-3 h-3 mr-1" />
                                      {tag.name}
                                      <button
                                        type="button"
                                        onClick={() => handleRemoveTag(tagId)}
                                        className="ml-2 text-purple-600 hover:text-purple-800 p-0.5 rounded-full hover:bg-purple-200 transition-colors duration-200"
                                      >
                                        <svg
                                          className="w-3 h-3"
                                          fill="none"
                                          stroke="currentColor"
                                          viewBox="0 0 24 24"
                                        >
                                          <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M6 18L18 6M6 6l12 12"
                                          />
                                        </svg>
                                      </button>
                                    </div>
                                  ) : null;
                                })}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Author & Student Assignment */}
                <div className="mt-8">
                  <div className="border-b border-gray-200 pb-4">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg">
                        <UsersIcon className="h-6 w-6 text-blue-600" />
                      </div>
                      <div>
                        <h4 className="text-xl font-semibold text-gray-900">
                          Author & Student Assignment
                        </h4>
                        <p className="text-sm text-gray-600 mt-1">
                          Assign course instructor and manage student
                          enrollments
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="mt-6 space-y-8">
                    {/* Author Selection */}
                    <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="p-2 bg-blue-100 rounded-lg">
                          <AcademicCapIcon className="h-5 w-5 text-blue-600" />
                        </div>
                        <div>
                          <h5 className="text-lg font-semibold text-gray-900">
                            Course Instructor
                          </h5>
                          <p className="text-sm text-gray-600">
                            {mode === "add"
                              ? "You are automatically set as the instructor"
                              : "Select the primary instructor for this course"}
                          </p>
                        </div>
                      </div>

                      {mode === "view" ? (
                        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-4">
                          {(() => {
                            const authorId =
                              selectedAuthor ||
                              course?.author_id ||
                              course?.author?.id;
                            const authorUser = authorId
                              ? getUserById(authorId)
                              : null;

                            return authorUser ? (
                              <div className="flex items-center">
                                <div className="h-12 w-12 bg-blue-500 rounded-full flex items-center justify-center mr-4 shadow-sm">
                                  <UserIcon className="h-6 w-6 text-white" />
                                </div>
                                <div className="flex-1">
                                  <div className="flex items-center gap-2 mb-1">
                                    <h6 className="text-lg font-semibold text-gray-900">
                                      {authorUser.name}
                                    </h6>
                                    <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs font-medium rounded-full">
                                      {course?.author_details?.status ||
                                        authorUser.status ||
                                        "Online"}
                                    </span>
                                  </div>
                                  <div className="text-sm text-gray-600 mb-1">
                                    {authorUser.email}
                                  </div>
                                  <div className="flex items-center gap-2 text-sm text-gray-500">
                                    <span className="flex items-center gap-1">
                                      <AcademicCapIcon className="h-4 w-4" />
                                      {authorUser.role || "Instructor"}
                                    </span>
                                    {course?.zoom_id && (
                                      <span className="flex items-center gap-1">
                                        <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                                        Zoom Available
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </div>
                            ) : (
                              <div className="text-center py-6">
                                <div className="h-16 w-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
                                  <UserIcon className="h-8 w-8 text-gray-400" />
                                </div>
                                <p className="text-gray-500 font-medium">
                                  No instructor assigned
                                </p>
                                <p className="text-sm text-gray-400">
                                  Please select an instructor to continue
                                </p>
                              </div>
                            );
                          })()}
                        </div>
                      ) : (
                        <div className="space-y-4">
                          {/* Show automatic instructor selection info for new courses */}
                          {mode === "add" &&
                            currentUser &&
                            selectedAuthor === currentUser.uid && (
                              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                                <div className="flex items-center gap-2 text-blue-700">
                                  <AcademicCapIcon className="h-4 w-4" />
                                  <span className="text-sm font-medium">
                                    You are automatically set as the course
                                    instructor
                                  </span>
                                </div>
                                <p className="text-xs text-blue-600 mt-1">
                                  As the course creator, you will be the primary
                                  instructor
                                </p>
                              </div>
                            )}

                          <div className="relative">
                            <select
                              value={selectedAuthor}
                              onChange={(e) =>
                                setSelectedAuthor(e.target.value)
                              }
                              className="w-full px-4 py-4 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-50 disabled:text-gray-500 transition-all duration-200 appearance-none bg-white text-lg"
                              disabled={usersLoading}
                            >
                              <option value="">Choose an instructor...</option>
                              {authors.map((author) => (
                                <option key={author.id} value={author.id}>
                                  {author.name} • {author.email} •{" "}
                                  {author.role || "Instructor"}
                                </option>
                              ))}
                            </select>
                            <div className="absolute inset-y-0 right-0 flex items-center pr-4 pointer-events-none">
                              <AcademicCapIcon className="h-5 w-5 text-gray-400" />
                            </div>
                          </div>

                          {/* Show current user info when they are automatically selected */}
                          {mode === "add" &&
                            currentUser &&
                            selectedAuthor === currentUser.uid && (
                              <div className="text-sm text-gray-600">
                                <span className="font-medium">
                                  Current selection:
                                </span>{" "}
                                {currentUser.displayName || currentUser.email}{" "}
                                (You - Course Creator)
                                <br />
                                <span className="text-xs text-gray-500">
                                  You can change this selection if needed
                                </span>
                              </div>
                            )}

                          {/* Show validation message if no instructor is selected */}
                          {mode === "add" && !selectedAuthor && (
                            <div className="text-sm text-red-600">
                              <span className="font-medium">
                                ⚠️ Instructor required:
                              </span>{" "}
                              Please select an instructor for this course
                            </div>
                          )}

                          {usersLoading && (
                            <div className="flex items-center gap-2 text-blue-600">
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                              <span className="text-sm">
                                Loading instructors...
                              </span>
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Student Assignment */}
                    <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="p-2 bg-green-100 rounded-lg">
                          <UsersIcon className="h-5 w-5 text-green-600" />
                        </div>
                        <div className="flex-1">
                          <h5 className="text-lg font-semibold text-gray-900">
                            Enrolled Students
                          </h5>
                          <p className="text-sm text-gray-600">
                            Manage student enrollments and course access
                          </p>
                        </div>
                        {mode !== "view" && (
                          <div className="px-3 py-1 bg-green-100 text-green-700 text-sm font-medium rounded-full">
                            {selectedStudents.length} enrolled
                          </div>
                        )}
                      </div>

                      {mode === "view" ? (
                        <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg p-4">
                          {(() => {
                            const enrolledStudents =
                              selectedStudents.length > 0
                                ? selectedStudents
                                : course?.enrolled_students || [];

                            return enrolledStudents.length > 0 ? (
                              <div className="space-y-3">
                                {enrolledStudents
                                  .slice(0, 6)
                                  .map((studentId) => {
                                    const student = getUserById(studentId);
                                    return student ? (
                                      <div
                                        key={studentId}
                                        className="flex items-center p-3 bg-white rounded-lg border border-green-100 shadow-sm"
                                      >
                                        <div className="h-10 w-10 bg-green-500 rounded-full flex items-center justify-center mr-3 shadow-sm">
                                          <UserIcon className="h-5 w-5 text-white" />
                                        </div>
                                        <div className="flex-1">
                                          <div className="font-semibold text-gray-900">
                                            {student.name}
                                          </div>
                                          <div className="text-sm text-gray-600">
                                            {student.email}
                                          </div>
                                        </div>
                                        <div className="flex items-center gap-1 text-green-600">
                                          <CheckCircleIcon className="h-4 w-4" />
                                          <span className="text-xs font-medium">
                                            Enrolled
                                          </span>
                                        </div>
                                      </div>
                                    ) : (
                                      <div
                                        key={studentId}
                                        className="flex items-center p-3 bg-gray-50 rounded-lg border border-gray-200"
                                      >
                                        <div className="h-10 w-10 bg-gray-300 rounded-full flex items-center justify-center mr-3">
                                          <UserIcon className="h-5 w-5 text-gray-500" />
                                        </div>
                                        <div className="flex-1">
                                          <span className="text-gray-400 text-sm">
                                            Student not found (ID: {studentId})
                                          </span>
                                        </div>
                                      </div>
                                    );
                                  })}
                                {enrolledStudents.length > 6 && (
                                  <div className="text-center py-3">
                                    <div className="inline-flex items-center gap-2 px-4 py-2 bg-green-100 text-green-700 rounded-full text-sm font-medium">
                                      <UsersIcon className="h-4 w-4" />+
                                      {enrolledStudents.length - 6} more
                                      students
                                    </div>
                                  </div>
                                )}
                              </div>
                            ) : (
                              <div className="text-center py-8">
                                <div className="h-16 w-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
                                  <UsersIcon className="h-8 w-8 text-gray-400" />
                                </div>
                                <p className="text-gray-500 font-medium">
                                  No students enrolled
                                </p>
                                <p className="text-sm text-gray-400">
                                  Students will appear here once enrolled
                                </p>
                              </div>
                            );
                          })()}
                        </div>
                      ) : (
                        <div className="space-y-4">
                          <div className="relative">
                            <select
                              multiple
                              value={selectedStudents}
                              onChange={(e) => {
                                const values = Array.from(
                                  e.target.selectedOptions,
                                  (option) => option.value
                                );
                                setSelectedStudents(values);
                              }}
                              className="w-full h-40 px-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 disabled:bg-gray-50 disabled:text-gray-500 transition-all duration-200 bg-white"
                              disabled={usersLoading}
                            >
                              {students.map((student) => (
                                <option key={student.id} value={student.id}>
                                  {student.name} • {student.email}
                                </option>
                              ))}
                            </select>
                          </div>

                          <div className="flex items-center justify-between text-sm">
                            <div className="flex items-center gap-2 text-gray-600">
                              <InformationCircleIcon className="h-4 w-4" />
                              <span>
                                Hold Ctrl/Cmd to select multiple students
                              </span>
                            </div>
                            {usersLoading && (
                              <div className="flex items-center gap-2 text-green-600">
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-green-600"></div>
                                <span>Loading students...</span>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Additional Author Details */}
                    {selectedAuthor && mode !== "view" && (
                      <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
                        <div className="flex items-center gap-3 mb-4">
                          <div className="p-2 bg-purple-100 rounded-lg">
                            <SparklesIcon className="h-5 w-5 text-purple-600" />
                          </div>
                          <h5 className="text-lg font-semibold text-gray-900">
                            Additional Instructor Details
                          </h5>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          <div className="space-y-3">
                            <label className="block text-sm font-medium text-gray-700">
                              Zoom Meeting Details
                            </label>
                            <div className="relative">
                              <textarea
                                {...register("zoom_id")}
                                rows="3"
                                className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all duration-200 resize-none"
                                placeholder="Enter Zoom meeting ID, password, and any additional details..."
                              />
                              <div className="absolute top-3 right-3 text-gray-400">
                                <div className="w-6 h-6 bg-gray-100 rounded-full flex items-center justify-center">
                                  <span className="text-xs font-bold">Z</span>
                                </div>
                              </div>
                            </div>
                          </div>

                          <div className="space-y-3">
                            <label className="block text-sm font-medium text-gray-700">
                              Availability Status
                            </label>
                            <div className="relative">
                              <select
                                {...register("author_status")}
                                className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all duration-200 appearance-none bg-white"
                              >
                                <option value="Online">
                                  🟢 Online - Available for questions
                                </option>
                                <option value="Offline">
                                  🔴 Offline - Not available
                                </option>
                                <option value="Busy">
                                  🟡 Busy - Limited availability
                                </option>
                              </select>
                              <div className="absolute inset-y-0 right-0 flex items-center pr-4 pointer-events-none">
                                <ClockIcon className="h-5 w-5 text-gray-400" />
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Course Content */}
                <div className="mt-8">
                  <div className="border-b border-gray-200 pb-4">
                    <h4 className="text-xl font-semibold text-gray-900">
                      Course Content
                    </h4>
                    <p className="text-sm text-gray-600 mt-1">
                      Course description, requirements, and learning outcomes
                    </p>
                  </div>

                  <div className="mt-6 space-y-6">
                    {/* Summary */}
                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-gray-700">
                        Course Summary
                      </label>
                      <textarea
                        {...register("summary")}
                        disabled={mode === "view"}
                        rows="3"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-50 disabled:text-gray-500 transition-colors duration-200 resize-none"
                        placeholder="Brief course summary..."
                      />
                    </div>

                    {/* Requirements */}
                    <div className="space-y-3">
                      <label className="block text-sm font-medium text-gray-700">
                        Course Requirements
                      </label>
                      {mode === "view" ? (
                        <div className="space-y-2">
                          {course?.meta?.requirements?.length > 0 ? (
                            <div className="flex flex-wrap gap-2">
                              {course.meta.requirements.map((req, index) => (
                                <span
                                  key={index}
                                  className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-blue-100 text-blue-800 border border-blue-200"
                                >
                                  {req}
                                </span>
                              ))}
                            </div>
                          ) : (
                            <p className="text-gray-500 text-sm">
                              No requirements specified
                            </p>
                          )}
                        </div>
                      ) : (
                        <div className="space-y-3">
                          <div className="flex gap-2">
                            <input
                              type="text"
                              value={newRequirement}
                              onChange={(e) =>
                                setNewRequirement(e.target.value)
                              }
                              onKeyPress={(e) =>
                                e.key === "Enter" && handleAddRequirement()
                              }
                              placeholder="Add a requirement..."
                              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                            />
                            <button
                              type="button"
                              onClick={handleAddRequirement}
                              disabled={!newRequirement.trim()}
                              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium transition-colors duration-200"
                            >
                              Add
                            </button>
                          </div>
                          {requirements.length > 0 && (
                            <div className="space-y-2">
                              {requirements.map((req, index) => (
                                <div
                                  key={index}
                                  className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200"
                                >
                                  <span className="text-sm text-gray-700">
                                    {req}
                                  </span>
                                  <button
                                    type="button"
                                    onClick={() =>
                                      handleRemoveRequirement(index)
                                    }
                                    className="text-red-500 hover:text-red-700 p-1 rounded transition-colors duration-200"
                                  >
                                    <svg
                                      className="w-4 h-4"
                                      fill="none"
                                      stroke="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M6 18L18 6M6 6l12 12"
                                      />
                                    </svg>
                                  </button>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Learning Outcomes */}
                    <div className="space-y-3">
                      <label className="block text-sm font-medium text-gray-700">
                        Learning Outcomes
                      </label>
                      {mode === "view" ? (
                        <div className="space-y-2">
                          {course?.meta?.learnings?.length > 0 ? (
                            <div className="space-y-2">
                              {course.meta.learnings.map((learning, index) => (
                                <div
                                  key={index}
                                  className="flex items-start space-x-2"
                                >
                                  <div className="flex-shrink-0 w-2 h-2 bg-green-500 rounded-full mt-2"></div>
                                  <span className="text-sm text-gray-700">
                                    {learning}
                                  </span>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p className="text-gray-500 text-sm">
                              No learning outcomes specified
                            </p>
                          )}
                        </div>
                      ) : (
                        <div className="space-y-3">
                          <div className="flex gap-2">
                            <input
                              type="text"
                              value={newLearning}
                              onChange={(e) => setNewLearning(e.target.value)}
                              onKeyPress={(e) =>
                                e.key === "Enter" && handleAddLearning()
                              }
                              placeholder="Add a learning outcome..."
                              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                            />
                            <button
                              type="button"
                              onClick={handleAddLearning}
                              disabled={!newLearning.trim()}
                              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium transition-colors duration-200"
                            >
                              Add
                            </button>
                          </div>
                          {learnings.length > 0 && (
                            <div className="space-y-2">
                              {learnings.map((learning, index) => (
                                <div
                                  key={index}
                                  className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200"
                                >
                                  <div className="flex items-start space-x-2 flex-1">
                                    <div className="flex-shrink-0 w-2 h-2 bg-green-500 rounded-full mt-2"></div>
                                    <span className="text-sm text-gray-700">
                                      {learning}
                                    </span>
                                  </div>
                                  <button
                                    type="button"
                                    onClick={() => handleRemoveLearning(index)}
                                    className="text-red-500 hover:text-red-700 p-1 rounded transition-colors duration-200 ml-2"
                                  >
                                    <svg
                                      className="w-4 h-4"
                                      fill="none"
                                      stroke="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M6 18L18 6M6 6l12 12"
                                      />
                                    </svg>
                                  </button>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Course Books */}
                    <div className="space-y-3">
                      <label className="block text-sm font-medium text-gray-700">
                        Course Books & Resources
                      </label>
                      {mode === "view" ? (
                        <div className="space-y-3">
                          {course?.books?.length > 0 ? (
                            <>
                              {/* Book Statistics */}
                              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200">
                                <div className="flex items-center space-x-4">
                                  <div className="text-center">
                                    <div className="text-lg font-semibold text-blue-600">
                                      {course.books.length}
                                    </div>
                                    <div className="text-xs text-gray-600">
                                      Total Books
                                    </div>
                                  </div>
                                  <div className="text-center">
                                    <div className="text-lg font-semibold text-green-600">
                                      ₵
                                      {course.books.reduce(
                                        (total, book) =>
                                          total +
                                          (parseFloat(book.amount) || 0),
                                        0
                                      )}
                                    </div>
                                    <div className="text-xs text-gray-600">
                                      Total Value
                                    </div>
                                  </div>
                                  <div className="text-center">
                                    <div className="text-lg font-semibold text-purple-600">
                                      {
                                        course.books.filter(
                                          (book) =>
                                            (parseFloat(book.amount) || 0) === 0
                                        ).length
                                      }
                                    </div>
                                    <div className="text-xs text-gray-600">
                                      Free Books
                                    </div>
                                  </div>
                                </div>
                              </div>

                              {/* Books Grid */}
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                {course.books.map((book, index) => (
                                  <div
                                    key={index}
                                    className="flex items-center p-3 bg-blue-50 rounded-lg border border-blue-200 hover:border-blue-300 transition-colors duration-200"
                                  >
                                    <div className="flex-shrink-0 mr-3">
                                      {book.image_url ? (
                                        <img
                                          src={book.image_url}
                                          alt={book.title}
                                          className="w-12 h-16 object-cover rounded border"
                                        />
                                      ) : (
                                        <div className="w-12 h-16 bg-blue-100 rounded border flex items-center justify-center">
                                          <BookOpenIcon className="h-6 w-6 text-blue-400" />
                                        </div>
                                      )}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <h4 className="text-sm font-medium text-gray-900 truncate">
                                        {book.title}
                                      </h4>
                                      <p className="text-xs text-gray-600 truncate">
                                        {book.description}
                                      </p>
                                      <div className="flex items-center justify-between mt-1">
                                        {book.amount && (
                                          <div className="flex items-center">
                                            <CurrencyDollarIcon className="h-4 w-4 text-green-600 mr-1" />
                                            <span className="text-sm font-semibold text-green-700">
                                              ₵{book.amount}
                                            </span>
                                          </div>
                                        )}
                                        {book.author && (
                                          <div className="text-xs text-gray-500 truncate">
                                            by {book.author}
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </>
                          ) : (
                            <div className="text-center py-6 border-2 border-dashed border-gray-300 rounded-lg bg-gray-50">
                              <BookOpenIcon className="h-12 w-12 mx-auto text-gray-400 mb-3" />
                              <p className="text-sm text-gray-600">
                                No books assigned to this course
                              </p>
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="space-y-3">
                          {/* Book Statistics */}
                          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200">
                            <div className="flex items-center space-x-4">
                              <div className="text-center">
                                <div className="text-lg font-semibold text-blue-600">
                                  {selectedBooks.length}
                                </div>
                                <div className="text-xs text-gray-600">
                                  Total Books
                                </div>
                              </div>
                              <div className="text-center">
                                <div className="text-lg font-semibold text-green-600">
                                  ₵
                                  {selectedBooks.reduce(
                                    (total, book) =>
                                      total + (parseFloat(book.amount) || 0),
                                    0
                                  )}
                                </div>
                                <div className="text-xs text-gray-600">
                                  Total Value
                                </div>
                              </div>
                              <div className="text-center">
                                <div className="text-lg font-semibold text-purple-600">
                                  {
                                    selectedBooks.filter(
                                      (book) =>
                                        (parseFloat(book.amount) || 0) === 0
                                    ).length
                                  }
                                </div>
                                <div className="text-xs text-gray-600">
                                  Free Books
                                </div>
                              </div>
                            </div>
                            <button
                              type="button"
                              onClick={() => setShowBookModal(true)}
                              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200"
                            >
                              <BookOpenIcon className="h-4 w-4 mr-2" />
                              Manage Books
                            </button>
                          </div>

                          {/* Selected Books Display */}
                          {selectedBooks.length > 0 ? (
                            <div className="space-y-2">
                              <div className="flex items-center justify-between">
                                <h4 className="text-sm font-medium text-gray-700">
                                  Selected Books ({selectedBooks.length})
                                </h4>
                                <button
                                  type="button"
                                  onClick={() => setSelectedBooks([])}
                                  className="text-sm text-red-600 hover:text-red-800 transition-colors duration-200"
                                >
                                  Clear All
                                </button>
                              </div>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                {selectedBooks.map((book, index) => (
                                  <div
                                    key={book.id}
                                    className="relative flex items-center p-3 bg-blue-50 rounded-lg border border-blue-200 hover:border-blue-300 transition-colors duration-200"
                                  >
                                    <div className="flex-shrink-0 mr-3">
                                      {book.image_url ? (
                                        <img
                                          src={book.image_url}
                                          alt={book.title}
                                          className="w-12 h-16 object-cover rounded border"
                                        />
                                      ) : (
                                        <div className="w-12 h-16 bg-blue-100 rounded border flex items-center justify-center">
                                          <BookOpenIcon className="h-6 w-6 text-blue-400" />
                                        </div>
                                      )}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <h4 className="text-sm font-medium text-gray-900 truncate">
                                        {book.title}
                                      </h4>
                                      <p className="text-xs text-gray-600 truncate">
                                        {book.description}
                                      </p>
                                      <div className="flex items-center justify-between mt-1">
                                        {book.amount && (
                                          <div className="flex items-center">
                                            <CurrencyDollarIcon className="h-4 w-4 text-green-600 mr-1" />
                                            <span className="text-sm font-semibold text-green-700">
                                              ₵{book.amount}
                                            </span>
                                          </div>
                                        )}
                                        {book.author && (
                                          <div className="text-xs text-gray-500 truncate">
                                            by {book.author}
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                    <button
                                      type="button"
                                      onClick={() => handleRemoveBook(book.id)}
                                      className="absolute top-2 right-2 p-1 text-red-500 hover:text-red-700 hover:bg-red-100 rounded-full transition-colors duration-200"
                                      title="Remove book"
                                    >
                                      <XMarkIcon className="h-4 w-4" />
                                    </button>
                                  </div>
                                ))}
                              </div>
                            </div>
                          ) : (
                            <div className="text-center py-6 border-2 border-dashed border-gray-300 rounded-lg bg-gray-50">
                              <BookOpenIcon className="h-12 w-12 mx-auto text-gray-400 mb-3" />
                              <p className="text-sm text-gray-600 mb-3">
                                No books selected for this course
                              </p>
                              <button
                                type="button"
                                onClick={() => setShowBookModal(true)}
                                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200"
                              >
                                <BookOpenIcon className="h-4 w-4 mr-2" />
                                Add Books
                              </button>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Manage Classrooms Section - Only show for edit mode and if user is admin or author */}
              {mode === "edit" &&
                course?.id &&
                (userRole === "admin" || userRole === "author") && (
                  <div className="mt-8">
                    <div className="border-b border-gray-200 pb-4">
                      <h4 className="text-xl font-semibold text-gray-900">
                        Classroom Management
                      </h4>
                      <p className="text-sm text-gray-600 mt-1">
                        Manage classrooms and student assignments for this
                        course
                      </p>
                    </div>
                    <div className="mt-6">
                      <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
                        <div className="flex items-center justify-between mb-4">
                          <div className="flex items-center space-x-3">
                            <div className="p-3 bg-purple-100 rounded-lg">
                              <AcademicCapIcon className="h-6 w-6 text-purple-600" />
                            </div>
                            <div>
                              <h5 className="text-lg font-medium text-gray-900">
                                Course Classrooms
                              </h5>
                              <p className="text-sm text-gray-600">
                                Create and manage virtual classrooms for this
                                course
                              </p>
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => setShowClassroomManager(true)}
                            className="inline-flex items-center px-4 py-3 rounded-lg border border-purple-300 bg-purple-50 text-purple-700 font-medium hover:bg-purple-100 hover:border-purple-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 transition-colors duration-200"
                          >
                            <AcademicCapIcon className="h-5 w-5 mr-2" />
                            Manage Classrooms
                          </button>
                        </div>

                        {/* Display Current Classrooms */}
                        <div className="mt-4">
                          <h6 className="text-sm font-medium text-gray-700 mb-3">
                            Current Classrooms ({courseClassrooms?.length || 0})
                          </h6>
                          {classroomsLoading ? (
                            <div className="text-center py-4">
                              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mx-auto"></div>
                              <p className="text-sm text-gray-500 mt-2">
                                Loading classrooms...
                              </p>
                            </div>
                          ) : courseClassrooms &&
                            courseClassrooms.length > 0 ? (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                              {courseClassrooms.map((classroom, index) => (
                                <div
                                  key={classroom.id || index}
                                  className="flex items-center p-3 bg-purple-50 rounded-lg border border-purple-200 hover:border-purple-300 transition-colors duration-200"
                                >
                                  <div className="flex-shrink-0 mr-3">
                                    <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                                      <AcademicCapIcon className="h-5 w-5 text-purple-600" />
                                    </div>
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <h6 className="text-sm font-medium text-gray-900 truncate">
                                      {classroom.name || "Unknown Classroom"}
                                    </h6>
                                    {classroom.subject && (
                                      <p className="text-xs text-gray-600 truncate">
                                        {classroom.subject}
                                      </p>
                                    )}
                                    {classroom.category && (
                                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800 mt-1">
                                        {classroom.category}
                                      </span>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div className="text-center py-6 border-2 border-dashed border-purple-300 rounded-lg bg-purple-50">
                              <AcademicCapIcon className="h-12 w-12 mx-auto text-purple-400 mb-3" />
                              <p className="text-sm text-gray-600">
                                No classrooms assigned to this course yet
                              </p>
                              <p className="text-xs text-gray-500 mt-1">
                                Click "Manage Classrooms" to add classrooms
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

              {/* Detailed Description */}
              <div className="mt-8">
                <div className="border-b border-gray-200 pb-4">
                  <h4 className="text-xl font-semibold text-gray-900">
                    Detailed Description
                  </h4>
                  <p className="text-sm text-gray-600 mt-1">
                    Provide a comprehensive description of your course
                  </p>
                </div>

                <div className="mt-6">
                  <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
                    {mode === "view" ? (
                      <div className="w-full max-h-96 overflow-y-auto p-6 border border-gray-300 rounded-lg bg-gray-50">
                        <div
                          dangerouslySetInnerHTML={{
                            __html:
                              course?.meta?.description ||
                              "No description available",
                          }}
                        />
                      </div>
                    ) : (
                      <div className="border border-gray-300 rounded-lg overflow-hidden">
                        <div className="relative">
                          <Editor
                            initialValue={course?.meta?.description || ""}
                            init={tinyMCEConfig}
                            onEditorChange={(content) =>
                              setValue("description", content)
                            }
                            onInit={() => {
                              // Additional safety check
                              try {
                                if (
                                  editorRef.current &&
                                  typeof editorRef.current === "object"
                                ) {
                                  console.log(
                                    "TinyMCE editor initialized successfully"
                                  );
                                }
                              } catch (error) {
                                console.warn("TinyMCE onInit error:", error);
                              }
                            }}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Course Structure - Sections & Lessons */}
              <div className="mt-8">
                <div className="border-b border-gray-200 pb-4">
                  <h4 className="text-xl font-semibold text-gray-900">
                    Course Structure
                  </h4>
                  <p className="text-sm text-gray-600 mt-1">
                    Organize your course into sections and lessons
                  </p>
                </div>

                <div className="mt-6 space-y-6">
                  {/* Add New Section */}
                  {mode !== "view" && (
                    <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="p-2 bg-blue-100 rounded-lg">
                          <BookOpenIcon className="h-5 w-5 text-blue-600" />
                        </div>
                        <h5 className="text-lg font-semibold text-gray-900">
                          Add New Section
                        </h5>
                      </div>

                      <div className="flex gap-4 items-end">
                        <div className="flex-1">
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Section Name
                          </label>
                          <input
                            type="text"
                            value={newSection.name}
                            onChange={(e) =>
                              setNewSection((prev) => ({
                                ...prev,
                                name: e.target.value,
                              }))
                            }
                            placeholder="Enter section name..."
                            className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
                          />
                        </div>
                        <button
                          type="button"
                          onClick={handleAddSection}
                          disabled={!newSection.name.trim()}
                          className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium transition-colors duration-200"
                        >
                          Add Section
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Sections List */}
                  <div className="space-y-4">
                    {sections.length === 0 ? (
                      <div className="text-center py-8 text-gray-500">
                        <BookOpenIcon className="h-12 w-12 mx-auto text-gray-300 mb-3" />
                        <p>No sections created yet</p>
                        <p className="text-sm">
                          Start by adding your first section above
                        </p>
                      </div>
                    ) : (
                      sections.map((section, sectionIndex) => (
                        <div
                          key={section.id}
                          className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden"
                        >
                          {/* Section Header */}
                          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 px-6 py-4 border-b border-gray-200">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                <div className="p-2 bg-blue-100 rounded-lg">
                                  <BookOpenIcon className="h-5 w-5 text-blue-600" />
                                </div>
                                <div>
                                  <h5 className="text-lg font-semibold text-gray-900">
                                    {editingSection?.id === section.id ? (
                                      <input
                                        type="text"
                                        value={editingSection.name}
                                        onChange={(e) =>
                                          setEditingSection((prev) => ({
                                            ...prev,
                                            name: e.target.value,
                                          }))
                                        }
                                        className="px-3 py-1 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                      />
                                    ) : (
                                      section.name
                                    )}
                                  </h5>
                                  <p className="text-sm text-gray-600">
                                    Section {section.order} •{" "}
                                    {section.lessons?.length || 0} lessons
                                  </p>
                                </div>
                              </div>

                              {mode !== "view" && (
                                <div className="flex items-center gap-2">
                                  {editingSection?.id === section.id ? (
                                    <>
                                      <button
                                        type="button"
                                        onClick={handleUpdateSection}
                                        className="px-3 py-1 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm font-medium transition-colors duration-200"
                                      >
                                        Save
                                      </button>
                                      <button
                                        type="button"
                                        onClick={() => setEditingSection(null)}
                                        className="px-3 py-1 bg-gray-500 text-white rounded-lg hover:bg-gray-600 text-sm font-medium transition-colors duration-200"
                                      >
                                        Cancel
                                      </button>
                                    </>
                                  ) : (
                                    <>
                                      <button
                                        type="button"
                                        onClick={() =>
                                          handleEditSection(section.id)
                                        }
                                        className="p-2 text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-lg transition-colors duration-200"
                                      >
                                        <PencilIcon className="h-4 w-4" />
                                      </button>
                                      <button
                                        type="button"
                                        onClick={() =>
                                          handleRemoveSection(section.id)
                                        }
                                        className="p-2 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors duration-200"
                                      >
                                        <XMarkIcon className="h-4 w-4" />
                                      </button>
                                    </>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>

                          {/* Section Content */}
                          <div className="p-6">
                            {/* Add New Lesson */}
                            {mode !== "view" && (
                              <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
                                <h6 className="text-sm font-medium text-gray-700 mb-3">
                                  Add New Lesson
                                </h6>
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                  <div>
                                    <label className="block text-xs font-medium text-gray-600 mb-1">
                                      Lesson Name
                                    </label>
                                    <input
                                      type="text"
                                      value={newLesson.name}
                                      onChange={(e) =>
                                        setNewLesson((prev) => ({
                                          ...prev,
                                          name: e.target.value,
                                        }))
                                      }
                                      placeholder="Lesson name..."
                                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                                    />
                                  </div>
                                  <div>
                                    <label className="block text-xs font-medium text-gray-600 mb-1">
                                      Content Type
                                    </label>
                                    <select
                                      value={newLesson.content_type}
                                      onChange={(e) =>
                                        setNewLesson((prev) => ({
                                          ...prev,
                                          content_type: e.target.value,
                                        }))
                                      }
                                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                                    >
                                      <option value="video">Video</option>
                                      <option value="text">Text</option>
                                      <option value="quiz">Quiz</option>
                                      <option value="assignment">
                                        Assignment
                                      </option>
                                      <option value="article">Article</option>
                                    </select>
                                  </div>
                                  <div>
                                    <label className="block text-xs font-medium text-gray-600 mb-1">
                                      Order
                                    </label>
                                    <input
                                      type="number"
                                      value={newLesson.order}
                                      onChange={(e) =>
                                        setNewLesson((prev) => ({
                                          ...prev,
                                          order: parseInt(e.target.value) || 0,
                                        }))
                                      }
                                      placeholder="Order..."
                                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                                    />
                                  </div>
                                </div>

                                <div className="mt-3 space-y-3">
                                  <div>
                                    <label className="block text-xs font-medium text-gray-600 mb-1">
                                      Description
                                    </label>
                                    <Editor
                                      value={newLesson.description}
                                      onEditorChange={(description) =>
                                        setNewLesson((prev) => ({
                                          ...prev,
                                          description,
                                        }))
                                      }
                                      init={{
                                        ...courseEditorConfig({
                                          height: 200,
                                          placeholder:
                                            "Write your lesson description here...",
                                        }),
                                        readonly: mode === "view",
                                      }}
                                    />
                                  </div>

                                  {newLesson.content_type === "video" && (
                                    <div
                                      onDragOver={(e) => {
                                        e.preventDefault();
                                        setNewLessonVideoDragOver(true);
                                      }}
                                      onDragLeave={(e) => {
                                        e.preventDefault();
                                        setNewLessonVideoDragOver(false);
                                      }}
                                      onDrop={handleNewLessonVideoDrop}
                                      className={`space-y-2 p-3 rounded-xl border-2 border-dashed transition-all duration-300 ${videoUploading || newLessonVideoDragOver
                                          ? "border-blue-400 bg-blue-50 shadow-inner"
                                          : "bg-gray-50/50 border-gray-100 hover:border-blue-400 hover:bg-white"
                                        }`}
                                    >
                                      <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-2">
                                          <VideoCameraIcon className="h-4 w-4 text-blue-500" />
                                          <label className="block text-xs font-semibold text-gray-700">
                                            Lesson Video
                                          </label>
                                        </div>
                                        {!videoUploading && (
                                          <div className="flex items-center gap-3">
                                            {newLesson.video_url && (
                                              <button
                                                type="button"
                                                onClick={
                                                  handleNewLessonVideoDelete
                                                }
                                                className="text-[10px] font-bold text-red-600 hover:text-red-700 uppercase tracking-tight flex items-center gap-1"
                                                title="Delete Video"
                                              >
                                                <TrashIcon className="h-3 w-3" />
                                                Delete
                                              </button>
                                            )}
                                            <button
                                              type="button"
                                              onClick={() =>
                                                newLessonVideoInputRef.current?.click()
                                              }
                                              className="text-[10px] font-bold text-blue-600 hover:text-blue-700 uppercase tracking-tight flex items-center gap-1"
                                            >
                                              <CloudArrowUpIcon className="h-3 w-3" />
                                              Upload File
                                            </button>
                                          </div>
                                        )}
                                      </div>
                                      <div className="relative group">
                                        <input
                                          type="url"
                                          value={newLesson.video_url}
                                          onChange={(e) =>
                                            setNewLesson((prev) => ({
                                              ...prev,
                                              video_url: e.target.value,
                                            }))
                                          }
                                          disabled={videoUploading}
                                          placeholder="Enter video URL or drag and drop a file"
                                          className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm disabled:bg-gray-50 transition-all shadow-sm"
                                        />
                                        <input
                                          type="file"
                                          ref={newLessonVideoInputRef}
                                          onChange={handleNewLessonVideoUpload}
                                          accept="video/*"
                                          className="hidden"
                                        />
                                      </div>
                                      {videoUploading && (
                                        <div className="space-y-1.5 px-1 py-1">
                                          <div className="flex justify-between text-[10px] text-blue-600 font-bold uppercase tracking-wider">
                                            <span>Uploading video...</span>
                                            <span>
                                              {Math.round(videoUploadProgress)}%
                                            </span>
                                          </div>
                                          <div className="w-full bg-blue-100 rounded-full h-1 overflow-hidden">
                                            <div
                                              className="bg-blue-600 h-full rounded-full transition-all duration-300"
                                              style={{
                                                width: `${videoUploadProgress}%`,
                                              }}
                                            ></div>
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  )}

                                  {newLesson.content_type === "quiz" && (
                                    <div className="space-y-4">
                                      <div className="flex items-center justify-between">
                                        <label className="block text-xs font-medium text-gray-600">
                                          Quiz Questions
                                        </label>
                                        <button
                                          type="button"
                                          onClick={() => addQuizQuestion()}
                                          className="px-3 py-1 bg-blue-600 text-white text-xs rounded-lg hover:bg-blue-700 transition-colors duration-200"
                                        >
                                          + Add Question
                                        </button>
                                      </div>

                                      {newLesson.quiz.length === 0 ? (
                                        <div className="text-center py-4 text-gray-500 text-sm border-2 border-dashed border-gray-300 rounded-lg">
                                          No quiz questions added yet. Click
                                          "Add Question" to get started.
                                        </div>
                                      ) : (
                                        <div className="space-y-3">
                                          {newLesson.quiz.map(
                                            (question, qIndex) => (
                                              <div
                                                key={question.id}
                                                className="p-4 border border-gray-200 rounded-lg bg-gray-50"
                                              >
                                                <div className="flex items-center justify-between mb-3">
                                                  <span className="text-xs font-medium text-gray-700">
                                                    Question {qIndex + 1}
                                                  </span>
                                                  <button
                                                    type="button"
                                                    onClick={() =>
                                                      removeQuizQuestion(
                                                        question.id
                                                      )
                                                    }
                                                    className="text-red-600 hover:text-red-700 text-xs"
                                                  >
                                                    Remove
                                                  </button>
                                                </div>

                                                <div className="space-y-3">
                                                  <div>
                                                    <label className="block text-xs font-medium text-gray-600 mb-1">
                                                      Question
                                                    </label>
                                                    <input
                                                      type="text"
                                                      value={question.question}
                                                      onChange={(e) =>
                                                        updateQuizQuestion(
                                                          question.id,
                                                          "question",
                                                          e.target.value
                                                        )
                                                      }
                                                      placeholder="Enter your question..."
                                                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                                                    />
                                                  </div>

                                                  <div>
                                                    <label className="block text-xs font-medium text-gray-600 mb-2">
                                                      Options
                                                    </label>
                                                    <div className="space-y-2">
                                                      {question.options.map(
                                                        (option, optIndex) => (
                                                          <div
                                                            key={optIndex}
                                                            className="flex items-center gap-2"
                                                          >
                                                            <input
                                                              type="radio"
                                                              name={`correct_${question.id}`}
                                                              checked={
                                                                question.correct_ans_index ===
                                                                optIndex
                                                              }
                                                              onChange={() =>
                                                                updateQuizQuestion(
                                                                  question.id,
                                                                  "correct_ans_index",
                                                                  optIndex
                                                                )
                                                              }
                                                              className="text-blue-600 focus:ring-blue-500"
                                                            />
                                                            <input
                                                              type="text"
                                                              value={option}
                                                              onChange={(e) =>
                                                                updateQuizOption(
                                                                  question.id,
                                                                  optIndex,
                                                                  e.target.value
                                                                )
                                                              }
                                                              placeholder={`Option ${optIndex + 1
                                                                }`}
                                                              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                                                            />
                                                          </div>
                                                        )
                                                      )}
                                                    </div>
                                                  </div>
                                                </div>
                                              </div>
                                            )
                                          )}
                                        </div>
                                      )}
                                    </div>
                                  )}
                                </div>

                                <div className="mt-4">
                                  <button
                                    type="button"
                                    onClick={() => handleAddLesson(section.id)}
                                    disabled={!newLesson.name.trim()}
                                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium transition-colors duration-200"
                                  >
                                    Add Lesson
                                  </button>
                                </div>
                              </div>
                            )}

                            {/* Lessons List */}
                            <div className="space-y-3">
                              {section.lessons?.length === 0 ? (
                                <div className="text-center py-6 text-gray-500">
                                  <p className="text-sm">
                                    No lessons in this section yet
                                  </p>
                                </div>
                              ) : (
                                section.lessons?.map((lesson, lessonIndex) => (
                                  <div
                                    key={lesson.id}
                                    className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200"
                                  >
                                    <div className="flex items-center gap-3 flex-1">
                                      <div className="p-2 bg-green-100 rounded-lg">
                                        <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                                      </div>
                                      <div className="flex-1">
                                        <div className="flex items-center gap-2 mb-1">
                                          <h6 className="font-medium text-gray-900">
                                            {editingLesson?.id === lesson.id ? (
                                              <input
                                                type="text"
                                                value={editingLesson.name}
                                                onChange={(e) =>
                                                  setEditingLesson((prev) => ({
                                                    ...prev,
                                                    name: e.target.value,
                                                  }))
                                                }
                                                className="px-2 py-1 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                                              />
                                            ) : (
                                              lesson.name
                                            )}
                                          </h6>
                                          <span className="text-xs text-gray-500 bg-gray-200 px-2 py-1 rounded-full">
                                            {lesson.content_type}
                                          </span>
                                          <span className="text-xs text-gray-500 bg-blue-100 px-2 py-1 rounded-full">
                                            Order: {lesson.order}
                                          </span>
                                        </div>

                                        {editingLesson?.id === lesson.id ? (
                                          <div className="space-y-2">
                                            <Editor
                                              value={editingLesson.description}
                                              onEditorChange={(description) =>
                                                setEditingLesson((prev) => ({
                                                  ...prev,
                                                  description,
                                                }))
                                              }
                                              init={{
                                                ...courseEditorConfig({
                                                  height: 150,
                                                  placeholder:
                                                    "Write your lesson description here...",
                                                }),
                                                readonly: mode === "view",
                                              }}
                                            />
                                            {editingLesson.content_type ===
                                              "video" && (
                                                <div
                                                  onDragOver={(e) => {
                                                    e.preventDefault();
                                                    setEditingLessonVideoDragOver(
                                                      true
                                                    );
                                                  }}
                                                  onDragLeave={(e) => {
                                                    e.preventDefault();
                                                    setEditingLessonVideoDragOver(
                                                      false
                                                    );
                                                  }}
                                                  onDrop={
                                                    handleEditingLessonVideoDrop
                                                  }
                                                  className={`space-y-1.5 p-2 rounded-lg border-2 border-dashed transition-all duration-300 ${videoUploading ||
                                                      editingLessonVideoDragOver
                                                      ? "border-blue-400 bg-blue-50 shadow-inner"
                                                      : "bg-gray-50/50 border-gray-100 hover:border-blue-400 hover:bg-white"
                                                    }`}
                                                >
                                                  <div className="flex items-center justify-between">
                                                    <div className="flex items-center gap-1.5">
                                                      <VideoCameraIcon className="h-3.5 w-3.5 text-blue-500" />
                                                      <label className="block text-[10px] font-semibold text-gray-600">
                                                        Lesson Video
                                                      </label>
                                                    </div>
                                                    {!videoUploading && (
                                                      <div className="flex items-center gap-2">
                                                        {editingLesson.video_url && (
                                                          <button
                                                            type="button"
                                                            onClick={
                                                              handleEditingLessonVideoDelete
                                                            }
                                                            className="text-[9px] font-bold text-red-600 hover:text-red-700 uppercase tracking-tight flex items-center gap-1"
                                                            title="Delete Video"
                                                          >
                                                            <TrashIcon className="h-2.5 w-2.5" />
                                                            Delete
                                                          </button>
                                                        )}
                                                        <button
                                                          type="button"
                                                          onClick={() =>
                                                            editingLessonVideoInputRef.current?.click()
                                                          }
                                                          className="text-[9px] font-bold text-blue-600 hover:text-blue-700 uppercase tracking-tight flex items-center gap-1"
                                                        >
                                                          <CloudArrowUpIcon className="h-2.5 w-2.5" />
                                                          Upload
                                                        </button>
                                                      </div>
                                                    )}
                                                  </div>
                                                  <div className="relative group">
                                                    <input
                                                      type="url"
                                                      value={
                                                        editingLesson.video_url
                                                      }
                                                      onChange={(e) =>
                                                        setEditingLesson(
                                                          (prev) => ({
                                                            ...prev,
                                                            video_url:
                                                              e.target.value,
                                                          })
                                                        )
                                                      }
                                                      disabled={videoUploading}
                                                      placeholder="URL or drag and drop file"
                                                      className="w-full px-2 py-1.5 bg-white border border-gray-200 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-[11px] disabled:bg-gray-50 transition-all shadow-sm"
                                                    />
                                                    <input
                                                      type="file"
                                                      ref={
                                                        editingLessonVideoInputRef
                                                      }
                                                      onChange={
                                                        handleEditingLessonVideoUpload
                                                      }
                                                      accept="video/*"
                                                      className="hidden"
                                                    />
                                                  </div>
                                                  {videoUploading && (
                                                    <div className="space-y-1 px-1">
                                                      <div className="flex justify-between text-[9px] text-blue-600 font-bold uppercase">
                                                        <span>Uploading...</span>
                                                        <span>
                                                          {Math.round(
                                                            videoUploadProgress
                                                          )}
                                                          %
                                                        </span>
                                                      </div>
                                                      <div className="w-full bg-blue-100 rounded-full h-1 overflow-hidden">
                                                        <div
                                                          className="bg-blue-600 h-full rounded-full transition-all duration-300"
                                                          style={{
                                                            width: `${videoUploadProgress}%`,
                                                          }}
                                                        ></div>
                                                      </div>
                                                    </div>
                                                  )}
                                                </div>
                                              )}

                                            {editingLesson.content_type ===
                                              "quiz" && (
                                                <div className="space-y-4">
                                                  <div className="flex items-center justify-between">
                                                    <label className="block text-xs font-medium text-gray-600">
                                                      Quiz Questions
                                                    </label>
                                                    <button
                                                      type="button"
                                                      onClick={() =>
                                                        addQuizQuestion(
                                                          editingLesson.id,
                                                          section.id
                                                        )
                                                      }
                                                      className="px-2 py-1 bg-blue-600 text-white text-xs rounded-lg hover:bg-blue-700 transition-colors duration-200"
                                                    >
                                                      + Add
                                                    </button>
                                                  </div>

                                                  {!editingLesson.quiz ||
                                                    editingLesson.quiz.length ===
                                                    0 ? (
                                                    <div className="text-center py-3 text-gray-500 text-xs border border-dashed border-gray-300 rounded">
                                                      No quiz questions. Click
                                                      "Add" to create one.
                                                    </div>
                                                  ) : (
                                                    <div className="space-y-2">
                                                      {editingLesson.quiz.map(
                                                        (question, qIndex) => (
                                                          <div
                                                            key={question.id}
                                                            className="p-3 border border-gray-200 rounded bg-gray-50"
                                                          >
                                                            <div className="flex items-center justify-between mb-2">
                                                              <span className="text-xs font-medium text-gray-700">
                                                                Q{qIndex + 1}
                                                              </span>
                                                              <button
                                                                type="button"
                                                                onClick={() =>
                                                                  removeQuizQuestion(
                                                                    question.id,
                                                                    editingLesson.id,
                                                                    section.id
                                                                  )
                                                                }
                                                                className="text-red-600 hover:text-red-700 text-xs"
                                                              >
                                                                ×
                                                              </button>
                                                            </div>

                                                            <div className="space-y-2">
                                                              <input
                                                                type="text"
                                                                value={
                                                                  question.question
                                                                }
                                                                onChange={(e) =>
                                                                  updateQuizQuestion(
                                                                    question.id,
                                                                    "question",
                                                                    e.target
                                                                      .value,
                                                                    editingLesson.id,
                                                                    section.id
                                                                  )
                                                                }
                                                                placeholder="Question..."
                                                                className="w-full px-2 py-1 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-xs"
                                                              />

                                                              <div className="space-y-1">
                                                                {question.options.map(
                                                                  (
                                                                    option,
                                                                    optIndex
                                                                  ) => (
                                                                    <div
                                                                      key={
                                                                        optIndex
                                                                      }
                                                                      className="flex items-center gap-1"
                                                                    >
                                                                      <input
                                                                        type="radio"
                                                                        name={`correct_edit_${question.id}`}
                                                                        checked={
                                                                          question.correct_ans_index ===
                                                                          optIndex
                                                                        }
                                                                        onChange={() =>
                                                                          updateQuizQuestion(
                                                                            question.id,
                                                                            "correct_ans_index",
                                                                            optIndex,
                                                                            editingLesson.id,
                                                                            section.id
                                                                          )
                                                                        }
                                                                        className="text-blue-600 focus:ring-blue-500"
                                                                      />
                                                                      <input
                                                                        type="text"
                                                                        value={
                                                                          option
                                                                        }
                                                                        onChange={(
                                                                          e
                                                                        ) =>
                                                                          updateQuizOption(
                                                                            question.id,
                                                                            optIndex,
                                                                            e
                                                                              .target
                                                                              .value,
                                                                            editingLesson.id,
                                                                            section.id
                                                                          )
                                                                        }
                                                                        placeholder={`Option ${optIndex +
                                                                          1
                                                                          }`}
                                                                        className="flex-1 px-2 py-1 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-xs"
                                                                      />
                                                                    </div>
                                                                  )
                                                                )}
                                                              </div>
                                                            </div>
                                                          </div>
                                                        )
                                                      )}
                                                    </div>
                                                  )}
                                                </div>
                                              )}
                                          </div>
                                        ) : (
                                          <div>
                                            <div className="text-sm text-gray-600">
                                              {lesson.description ? (
                                                <div
                                                  className="prose prose-sm max-w-none"
                                                  dangerouslySetInnerHTML={{
                                                    __html: lesson.description,
                                                  }}
                                                />
                                              ) : (
                                                <p>No description</p>
                                              )}
                                            </div>

                                            {/* Display quiz content in view mode */}
                                            {lesson.content_type === "quiz" &&
                                              lesson.quiz &&
                                              lesson.quiz.length > 0 && (
                                                <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                                                  <h6 className="text-xs font-medium text-blue-800 mb-2">
                                                    Quiz Questions (
                                                    {lesson.quiz.length})
                                                  </h6>
                                                  <div className="space-y-2">
                                                    {lesson.quiz.map(
                                                      (question, qIndex) => (
                                                        <div
                                                          key={question.id}
                                                          className="p-2 bg-white border border-blue-100 rounded"
                                                        >
                                                          <p className="text-xs font-medium text-gray-800 mb-1">
                                                            Q{qIndex + 1}:{" "}
                                                            {question.question}
                                                          </p>
                                                          <div className="space-y-1">
                                                            {question.options.map(
                                                              (
                                                                option,
                                                                optIndex
                                                              ) => (
                                                                <div
                                                                  key={optIndex}
                                                                  className="flex items-center gap-2"
                                                                >
                                                                  <div
                                                                    className={`w-2 h-2 rounded-full ${optIndex ===
                                                                      question.correct_ans_index
                                                                      ? "bg-green-500"
                                                                      : "bg-gray-300"
                                                                      }`}
                                                                  ></div>
                                                                  <span
                                                                    className={`text-xs ${optIndex ===
                                                                      question.correct_ans_index
                                                                      ? "text-green-700 font-medium"
                                                                      : "text-gray-600"
                                                                      }`}
                                                                  >
                                                                    {option}
                                                                  </span>
                                                                </div>
                                                              )
                                                            )}
                                                          </div>
                                                        </div>
                                                      )
                                                    )}
                                                  </div>
                                                </div>
                                              )}

                                            {/* Display video URL in view mode */}
                                            {lesson.content_type === "video" &&
                                              lesson.video_url && (
                                                <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                                                  <h6 className="text-xs font-medium text-green-800 mb-1">
                                                    Video URL
                                                  </h6>
                                                  <a
                                                    href={lesson.video_url}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="text-xs text-green-600 hover:text-green-800 break-all"
                                                  >
                                                    {lesson.video_url}
                                                  </a>
                                                </div>
                                              )}
                                          </div>
                                        )}
                                      </div>
                                    </div>

                                    {mode !== "view" && (
                                      <div className="flex items-center gap-1">
                                        {editingLesson?.id === lesson.id ? (
                                          <>
                                            <button
                                              type="button"
                                              onClick={handleUpdateLesson}
                                              className="p-1 bg-green-600 text-white rounded hover:bg-green-700 text-xs transition-colors duration-200"
                                            >
                                              Save
                                            </button>
                                            <button
                                              type="button"
                                              onClick={() =>
                                                setEditingLesson(null)
                                              }
                                              className="p-1 bg-gray-500 text-white rounded hover:bg-gray-600 text-xs transition-colors duration-200"
                                            >
                                              Cancel
                                            </button>
                                          </>
                                        ) : (
                                          <>
                                            <button
                                              type="button"
                                              onClick={() =>
                                                handleEditLesson(
                                                  section.id,
                                                  lesson.id
                                                )
                                              }
                                              className="p-1 text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded transition-colors duration-200"
                                            >
                                              <PencilIcon className="h-3 w-3" />
                                            </button>
                                            <button
                                              type="button"
                                              onClick={() =>
                                                handleRemoveLesson(
                                                  section.id,
                                                  lesson.id
                                                )
                                              }
                                              className="p-1 text-red-600 hover:text-red-700 hover:bg-red-50 rounded transition-colors duration-200"
                                            >
                                              <XMarkIcon className="h-3 w-3" />
                                            </button>
                                          </>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                ))
                              )}
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </div>

              {/* Status Display */}
              {mode === "view" && (
                <div className="mt-6 flex items-center space-x-4">
                  <span
                    className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ring-1 ring-inset ${getStatusColor(
                      watchedStatus
                    )}`}
                  >
                    {watchedStatus === "live" && (
                      <CheckCircleIcon className="h-3 w-3 mr-1" />
                    )}
                    {watchedStatus === "draft" && (
                      <ClockIcon className="h-3 w-3 mr-1" />
                    )}
                    {watchedStatus === "archived" && (
                      <ExclamationTriangleIcon className="h-3 w-3 mr-1" />
                    )}
                    {watchedStatus?.charAt(0).toUpperCase() +
                      watchedStatus?.slice(1)}
                  </span>
                  <span
                    className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ring-1 ring-inset ${getPriceStatusColor(
                      watchedPriceStatus
                    )}`}
                  >
                    <CurrencyDollarIcon className="h-3 w-3 mr-1" />
                    {watchedPriceStatus?.charAt(0).toUpperCase() +
                      watchedPriceStatus?.slice(1)}
                    {watchedPriceStatus !== "free" && ` - ₵${watchedPrice}`}
                  </span>
                </div>
              )}
            </div>

            {/* Footer */}
            {mode !== "view" && (
              <div className="bg-gray-50 border-t border-gray-200 px-8 py-6">
                <div className="flex justify-between items-center">
                  <div className="flex items-center text-sm text-gray-600">
                    <InformationCircleIcon className="h-4 w-4 mr-2 text-blue-500" />
                    {mode === "add"
                      ? "All fields marked with * are required"
                      : "Changes will be saved immediately"}
                  </div>

                  <div className="flex space-x-4">
                    <button
                      type="button"
                      onClick={onClose}
                      className="px-6 py-3 rounded-lg border border-gray-300 bg-white text-gray-700 font-medium hover:bg-gray-50 hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors duration-200"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={loading}
                      className="inline-flex justify-center items-center px-8 py-3 rounded-lg border border-transparent bg-blue-600 text-white font-medium hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                    >
                      {loading ? (
                        <>
                          <svg
                            className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                          >
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                            ></circle>
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            ></path>
                          </svg>
                          {mode === "add" ? "Creating..." : "Updating..."}
                        </>
                      ) : (
                        <>
                          {mode === "add" ? (
                            <>
                              <SparklesIcon className="h-5 w-5 mr-2" />
                              Create Course
                            </>
                          ) : (
                            <>
                              <PencilIcon className="h-5 w-5 mr-2" />
                              Update Course
                            </>
                          )}
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </form>
        </div>
      </div>

      {/* Book Selection Modal */}
      {showBookModal && (
        <div className="fixed inset-0 z-60 overflow-y-auto">
          <div className="flex min-h-screen items-center justify-center p-4">
            <div
              className="fixed inset-0 bg-gradient-to-br from-gray-900/80 to-black/80 backdrop-blur-sm transition-opacity"
              onClick={() => setShowBookModal(false)}
            />

            <div className="relative w-full max-w-4xl transform rounded-2xl bg-white shadow-2xl transition-all border border-gray-100 overflow-hidden">
              {/* Header */}
              <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="p-2 bg-white/20 rounded-lg">
                      <BookOpenIcon className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold text-white">
                        Select Course Books & Resources
                      </h3>
                      <p className="text-blue-100 text-sm mt-1">
                        Choose books to include in this course
                      </p>
                      <p className="text-blue-200 text-xs mt-1">
                        💡 Course price will automatically update based on book
                        values
                      </p>
                    </div>
                  </div>

                  <button
                    onClick={() => setShowBookModal(false)}
                    className="p-2 rounded-lg text-white/80 hover:bg-white/20 hover:text-white transition-colors duration-200"
                    title="Close"
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>
              </div>

              {/* Content */}
              <div className="p-6">
                {/* Search Bar */}
                <div className="mb-6">
                  <div className="relative">
                    <input
                      type="text"
                      value={bookSearchTerm}
                      onChange={(e) => handleBookSearch(e.target.value)}
                      placeholder="Search books by title, description, or author..."
                      className="w-full px-4 py-3 pl-12 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
                    />
                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                      <svg
                        className="h-5 w-5 text-gray-400"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                        />
                      </svg>
                    </div>
                  </div>
                </div>

                {/* Books Grid */}
                <div className="space-y-4">
                  {booksLoading ? (
                    <div className="text-center py-12">
                      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                      <p className="text-gray-600">Loading books...</p>
                    </div>
                  ) : getFilteredBooks().length === 0 ? (
                    <div className="text-center py-12">
                      <BookOpenIcon className="h-16 w-16 mx-auto text-gray-300 mb-4" />
                      <p className="text-gray-600 font-medium">
                        No books found
                      </p>
                      <p className="text-sm text-gray-400">
                        {bookSearchTerm
                          ? "Try adjusting your search terms"
                          : "No books available in the system"}
                      </p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {getFilteredBooks().map((book) => {
                        const isSelected = selectedBooks.find(
                          (b) => b.id === book.id
                        );
                        return (
                          <div
                            key={book.id}
                            className={`relative p-4 border-2 rounded-lg transition-all duration-200 cursor-pointer hover:shadow-md ${isSelected
                              ? "border-blue-500 bg-blue-50 shadow-md"
                              : "border-gray-200 bg-white hover:border-gray-300"
                              }`}
                            onClick={() =>
                              isSelected
                                ? handleRemoveBook(book.id)
                                : handleAddBook(book)
                            }
                          >
                            {/* Selection Indicator */}
                            {isSelected && (
                              <div className="absolute top-3 right-3">
                                <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center">
                                  <CheckCircleIcon className="h-4 w-4 text-white" />
                                </div>
                              </div>
                            )}

                            {/* Book Cover */}
                            <div className="mb-3">
                              {book.image_url ? (
                                <img
                                  src={book.image_url}
                                  alt={book.title}
                                  className="w-full h-32 object-cover rounded-lg border border-gray-200"
                                />
                              ) : (
                                <div className="w-full h-32 bg-gradient-to-br from-blue-100 to-indigo-100 rounded-lg border border-gray-200 flex items-center justify-center">
                                  <BookOpenIcon className="h-12 w-12 text-blue-400" />
                                </div>
                              )}
                            </div>

                            {/* Book Info */}
                            <div className="space-y-2">
                              <h4 className="font-semibold text-gray-900 text-sm line-clamp-2 leading-tight">
                                {book.title}
                              </h4>
                              <p className="text-gray-600 text-xs line-clamp-2 leading-tight">
                                {book.description}
                              </p>

                              {/* Book Meta */}
                              <div className="flex items-center justify-between pt-2">
                                {book.amount && (
                                  <div className="flex items-center gap-1">
                                    <CurrencyDollarIcon className="h-4 w-4 text-green-600" />
                                    <span className="text-sm font-semibold text-green-700">
                                      ₵{book.amount}
                                    </span>
                                  </div>
                                )}
                                {book.author && (
                                  <div className="text-xs text-gray-500 truncate">
                                    by {book.author}
                                  </div>
                                )}
                              </div>

                              {/* Action Button */}
                              <button
                                type="button"
                                className={`w-full mt-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors duration-200 ${isSelected
                                  ? "bg-red-100 text-red-700 hover:bg-red-200 border border-red-200"
                                  : "bg-blue-100 text-blue-700 hover:bg-blue-200 border border-blue-200"
                                  }`}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  isSelected
                                    ? handleRemoveBook(book.id)
                                    : handleAddBook(book);
                                }}
                              >
                                {isSelected ? "Remove" : "Add to Course"}
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* Footer */}
                <div className="mt-6 pt-4 border-t border-gray-200">
                  {/* Price Impact Summary */}
                  {selectedBooks.length > 0 && (
                    <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center">
                          <div className="flex-shrink-0">
                            <svg
                              className="h-5 w-5 text-green-400"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1"
                              />
                            </svg>
                          </div>
                          <div className="ml-3">
                            <p className="text-sm text-green-800">
                              <strong>Price Impact:</strong> Course price will
                              be automatically set to ₵
                              {selectedBooks
                                .reduce(
                                  (total, book) =>
                                    total + (parseFloat(book.amount) || 0),
                                  0
                                )
                                .toFixed(2)}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="flex items-center justify-between">
                    <div className="text-sm text-gray-600">
                      {selectedBooks.length} book
                      {selectedBooks.length !== 1 ? "s" : ""} selected
                    </div>
                    <div className="flex gap-3">
                      <button
                        type="button"
                        onClick={() => setShowBookModal(false)}
                        className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors duration-200"
                      >
                        Done
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Course Classroom Manager */}
      {showClassroomManager && course?.id && (
        <CourseClassroomManager
          courseId={course.id}
          isOpen={showClassroomManager}
          onClose={() => setShowClassroomManager(false)}
        />
      )}

      {/* Custom Program Modal */}
      {showCustomProgramModal && (
        <div className="fixed inset-0 z-60 overflow-y-auto">
          <div className="flex min-h-screen items-center justify-center p-4">
            <div
              className="fixed inset-0 bg-black/50 backdrop-blur-sm"
              onClick={() => setShowCustomProgramModal(false)}
            />
            <div className="relative w-full max-w-md transform rounded-xl bg-white shadow-2xl transition-all">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">
                  Add Custom Program
                </h3>
                <p className="text-sm text-gray-600 mt-1">
                  Enter a new program name
                </p>
              </div>
              <div className="px-6 py-4">
                <input
                  type="text"
                  value={customProgram}
                  onChange={(e) => setCustomProgram(e.target.value)}
                  placeholder="Enter program name..."
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  onKeyPress={(e) =>
                    e.key === "Enter" && handleAddCustomProgram()
                  }
                />
              </div>
              <div className="px-6 py-4 border-t border-gray-200 flex gap-3">
                <button
                  type="button"
                  onClick={() => setShowCustomProgramModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleAddCustomProgram}
                  disabled={!customProgram.trim()}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
                >
                  Add Program
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Program Modal */}
      {showEditProgramModal && (
        <div className="fixed inset-0 z-60 overflow-y-auto">
          <div className="flex min-h-screen items-center justify-center p-4">
            <div
              className="fixed inset-0 bg-black/50 backdrop-blur-sm"
              onClick={() => setShowEditProgramModal(false)}
            />
            <div className="relative w-full max-w-md transform rounded-xl bg-white shadow-2xl transition-all">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">
                  Edit Program
                </h3>
                <p className="text-sm text-gray-600 mt-1">
                  Update program name:{" "}
                  <span className="font-medium text-gray-800">
                    {editingProgram}
                  </span>
                </p>
              </div>
              <div className="px-6 py-4">
                <input
                  type="text"
                  value={editProgram}
                  onChange={(e) => setEditProgram(e.target.value)}
                  placeholder="Enter new program name..."
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  onKeyPress={(e) => e.key === "Enter" && handleEditProgram()}
                />
              </div>
              <div className="px-6 py-4 border-t border-gray-200 flex gap-3">
                <button
                  type="button"
                  onClick={() => setShowEditProgramModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleEditProgram}
                  disabled={
                    !editProgram.trim() || editProgram === editingProgram
                  }
                  className="flex-1 px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
                >
                  Update Program
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Program Modal */}
      {showDeleteProgramModal && (
        <div className="fixed inset-0 z-60 overflow-y-auto">
          <div className="flex min-h-screen items-center justify-center p-4">
            <div
              className="fixed inset-0 bg-black/50 backdrop-blur-sm"
              onClick={() => setShowDeleteProgramModal(false)}
            />
            <div className="relative w-full max-w-md transform rounded-xl bg-white shadow-2xl transition-all">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">
                  Delete Program
                </h3>
                <p className="text-sm text-gray-600 mt-1">
                  Are you sure you want to delete the program:{" "}
                  <span className="font-medium text-red-800">
                    {deletingProgram}
                  </span>
                </p>
                <p className="text-xs text-red-600 mt-2">
                  ⚠️ This will also remove all custom categories associated with
                  this program.
                </p>
              </div>
              <div className="px-6 py-4 border-t border-gray-200 flex gap-3">
                <button
                  type="button"
                  onClick={() => setShowDeleteProgramModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleDeleteProgram}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                >
                  Delete Program
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Custom Category Modal */}
      {showCustomCategoryModal && (
        <div className="fixed inset-0 z-60 overflow-y-auto">
          <div className="flex min-h-screen items-center justify-center p-4">
            <div
              className="fixed inset-0 bg-black/50 backdrop-blur-sm"
              onClick={() => setShowCustomCategoryModal(false)}
            />
            <div className="relative w-full max-w-md transform rounded-xl bg-white shadow-2xl transition-all">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">
                  Add Custom Category
                </h3>
                <p className="text-sm text-gray-600 mt-1">
                  Enter a new category name for {selectedProgram}
                </p>
              </div>
              <div className="px-6 py-4">
                <input
                  type="text"
                  value={customCategory}
                  onChange={(e) => setCustomCategory(e.target.value)}
                  placeholder="Enter category name..."
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  onKeyPress={(e) =>
                    e.key === "Enter" && handleAddCustomCategory()
                  }
                />
              </div>
              <div className="px-6 py-4 border-t border-gray-200 flex gap-3">
                <button
                  type="button"
                  onClick={() => setShowCustomCategoryModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleAddCustomCategory}
                  disabled={!customCategory.trim()}
                  className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
                >
                  Add Category
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Category Modal */}
      {showEditCategoryModal && (
        <div className="fixed inset-0 z-60 overflow-y-auto">
          <div className="flex min-h-screen items-center justify-center p-4">
            <div
              className="fixed inset-0 bg-black/50 backdrop-blur-sm"
              onClick={() => setShowEditCategoryModal(false)}
            />
            <div className="relative w-full max-w-md transform rounded-xl bg-white shadow-2xl transition-all">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">
                  Edit Category
                </h3>
                <p className="text-sm text-gray-600 mt-1">
                  Update category name:{" "}
                  <span className="font-medium text-gray-800">
                    {editingCategory}
                  </span>
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  Program:{" "}
                  <span className="font-medium text-gray-700">
                    {selectedProgram}
                  </span>
                </p>
              </div>
              <div className="px-6 py-4">
                <input
                  type="text"
                  value={editCategory}
                  onChange={(e) => setEditCategory(e.target.value)}
                  placeholder="Enter new category name..."
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  onKeyPress={(e) => e.key === "Enter" && handleEditCategory()}
                />
              </div>
              <div className="px-6 py-4 border-t border-gray-200 flex gap-3">
                <button
                  type="button"
                  onClick={() => setShowEditCategoryModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleEditCategory}
                  disabled={
                    !editCategory.trim() || editCategory === editingCategory
                  }
                  className="flex-1 px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
                >
                  Update Category
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Category Modal */}
      {showDeleteCategoryModal && (
        <div className="fixed inset-0 z-60 overflow-y-auto">
          <div className="flex min-h-screen items-center justify-center p-4">
            <div
              className="fixed inset-0 bg-black/50 backdrop-blur-sm"
              onClick={() => setShowDeleteCategoryModal(false)}
            />
            <div className="relative w-full max-w-md transform rounded-xl bg-white shadow-2xl transition-all">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">
                  Delete Category
                </h3>
                <p className="text-sm text-gray-600 mt-1">
                  Are you sure you want to delete the category:{" "}
                  <span className="font-medium text-red-800">
                    {deletingCategory}
                  </span>
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  Program:{" "}
                  <span className="font-medium text-gray-700">
                    {selectedProgram}
                  </span>
                </p>
                <p className="text-xs text-red-600 mt-2">
                  ⚠️ This action cannot be undone.
                </p>
              </div>
              <div className="px-6 py-4 border-t border-gray-200 flex gap-3">
                <button
                  type="button"
                  onClick={() => setShowDeleteCategoryModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleDeleteCategory}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                >
                  Delete Category
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CourseModal;
